import { Component, ComponentType } from "@/types";
import { BaseCommandHandler, CommandResult, PropertyChange } from "../BaseCommandHandler";
import { textTemplates } from "../templates/TextTemplates";
import { useEditorStore } from "@/stores/editor/editor-store";
import { logger } from "@/utils/logger";
import type { TextSelectionContext, TextSegment } from "@/types/ai";
import { applyStylesToRange, isRichTextContent, segmentsToPlainText } from "@/types/ai";
import { BRAND_COLORS as BRAND_TEXT_COLORS } from "../constants/brand-colors";
import {
  TextStylePreset,
  TextPresetResult,
  EXTENDED_CSS_COLORS,
  SEMANTIC_COLORS,
  FONT_WEIGHT_MAP,
  FONT_FAMILY_MAP,
  DISPLAY_PRESETS,
  TEXT_PRESETS,
  LINE_HEIGHT_PRESETS,
  LETTER_SPACING_PRESETS,
  TEXT_SHADOW_PRESETS,
  GLOW_PRESETS,
  COLORED_SHADOW_MAP,
  DECORATION_STYLES,
  OUTLINE_COLORS,
  FLUID_SIZE_PRESETS,
} from "../constants/text-command-constants";
import {
  parseToRgb,
  adjustColorBrightness,
  extractShadowColor,
  buildTextShadow,
  extractShadowSize,
  buildTextShadowWithColor,
  clampFontSize,
  parseComponentFontSize,
  getNextTypographySize,
  getPreviousTypographySize,
} from "../utils/text-color-utils";

/** Safely extracts a string from an unknown Record value */
function asString(val: unknown): string | undefined {
  return typeof val === 'string' ? val : undefined;
}

/** Safely extracts a finite number from an unknown Record value */
function asNumber(val: unknown): number | undefined {
  return typeof val === 'number' && isFinite(val) ? val : undefined;
}

/** Safely extracts a string or number from an unknown Record value */
function asStringOrNumber(val: unknown): string | number | undefined {
  return typeof val === 'string' || typeof val === 'number' ? val : undefined;
}

type Breakpoint = 'mobile' | 'tablet' | 'desktop';

type HeadingLevelPreset = {
  fontSize: number;
  fontWeight: string;
  lineHeight: number;
};

const HEADING_LEVEL_PRESETS: Record<string, HeadingLevelPreset> = {
  h1: { fontSize: 32, fontWeight: 'bold', lineHeight: 1.2 },
  h2: { fontSize: 24, fontWeight: '600', lineHeight: 1.3 },
  h3: { fontSize: 18, fontWeight: '600', lineHeight: 1.4 },
  h4: { fontSize: 20, fontWeight: 'bold', lineHeight: 1.35 },
  h5: { fontSize: 18, fontWeight: 'bold', lineHeight: 1.4 },
  h6: { fontSize: 16, fontWeight: 'bold', lineHeight: 1.4 },
  body: { fontSize: 16, fontWeight: 'normal', lineHeight: 1.5 },
};

type TextSegmentStyle = NonNullable<TextSegment['style']>;

function parseBreakpoint(value: string): Breakpoint | undefined {
  const normalizedValue = value.toLowerCase();
  switch (normalizedValue) {
    case 'mobile':
    case 'tablet':
    case 'desktop':
      return normalizedValue;
    default:
      return undefined;
  }
}

type TextSegmentStyleKey =
  | 'fontWeight'
  | 'fontStyle'
  | 'textDecoration'
  | 'textDecorationLine'
  | 'textDecorationStyle'
  | 'textDecorationColor'
  | 'color'
  | 'backgroundColor'
  | 'fontSize'
  | 'fontFamily'
  | 'letterSpacing'
  | 'textTransform'
  | 'textShadow'
  | 'opacity';

function isTextSegmentStyleKey(key: string): key is TextSegmentStyleKey {
  switch (key) {
    case 'fontWeight':
    case 'fontStyle':
    case 'textDecoration':
    case 'textDecorationLine':
    case 'textDecorationStyle':
    case 'textDecorationColor':
    case 'color':
    case 'backgroundColor':
    case 'fontSize':
    case 'fontFamily':
    case 'letterSpacing':
    case 'textTransform':
    case 'textShadow':
    case 'opacity':
      return true;
    default:
      return false;
  }
}

function assignTextSegmentStyle(
  style: TextSegmentStyle,
  key: string,
  value: unknown
): void {
  if (!isTextSegmentStyleKey(key)) return;

  switch (key) {
    case 'fontWeight':
      if (typeof value === 'string' || typeof value === 'number') style.fontWeight = value;
      return;
    case 'fontStyle':
      if (typeof value === 'string') style.fontStyle = value;
      return;
    case 'textDecoration':
      if (typeof value === 'string') style.textDecoration = value;
      return;
    case 'textDecorationLine':
      if (typeof value === 'string') style.textDecorationLine = value;
      return;
    case 'textDecorationStyle':
      if (typeof value === 'string') style.textDecorationStyle = value;
      return;
    case 'textDecorationColor':
      if (typeof value === 'string') style.textDecorationColor = value;
      return;
    case 'color':
      if (typeof value === 'string') style.color = value;
      return;
    case 'backgroundColor':
      if (typeof value === 'string') style.backgroundColor = value;
      return;
    case 'fontSize':
      if (typeof value === 'string' || typeof value === 'number') style.fontSize = value;
      return;
    case 'fontFamily':
      if (typeof value === 'string') style.fontFamily = value;
      return;
    case 'letterSpacing':
      if (typeof value === 'string' || typeof value === 'number') style.letterSpacing = value;
      return;
    case 'textTransform':
      if (typeof value === 'string') style.textTransform = value;
      return;
    case 'textShadow':
      if (typeof value === 'string') style.textShadow = value;
      return;
    case 'opacity':
      if (typeof value === 'string' || typeof value === 'number') style.opacity = value;
      return;
  }
}

/**
 * Helper to get the latest component state from the store
 * This ensures we have the most up-to-date styles when removing decorations
 */
function getLatestComponentStyle(component: Component | undefined): { textDecorationLine?: string; textDecoration?: string } {
  if (!component?.id) return {};
  try {
    const store = useEditorStore.getState();
    const latestComponent = store.components.find(c => c.id === component.id);
    return {
      textDecorationLine: asString(latestComponent?.style?.textDecorationLine) ??
                         asString(latestComponent?.style?.textDecoration),
      textDecoration: asString(latestComponent?.properties?.textDecoration)
    };
  } catch {
    // Fallback to passed component if store access fails
    return {
      textDecorationLine: asString(component?.style?.textDecorationLine) ??
                         asString(component?.style?.textDecoration),
      textDecoration: asString(component?.properties?.textDecoration)
    };
  }
}

export function parseHeadingLevelPresetChange(command: string): PropertyChange | null {
  const normalizedCommand = command.trim().toLowerCase().replace(/\s+/g, ' ');
  const directMatch = normalizedCommand.match(/^h([1-6])$/);
  if (directMatch) {
    const preset = HEADING_LEVEL_PRESETS[`h${directMatch[1]}`];
    return preset ? { properties: { ...preset } } : null;
  }

  const levelMatch = normalizedCommand.match(
    /^(?:set|change|make)?\s*(?:the\s+)?(?:selected\s+)?(?:text\s+)?heading(?:\s+level)?\s*(?:to|as)?\s*(?:heading\s*)?(h?[1-6]|body|paragraph)$/,
  );
  if (!levelMatch) return null;

  const rawLevel = levelMatch[1] === 'paragraph' ? 'body' : levelMatch[1];
  const level = /^[1-6]$/.test(rawLevel) ? `h${rawLevel}` : rawLevel;
  const preset = HEADING_LEVEL_PRESETS[level];
  return preset ? { properties: { ...preset } } : null;
}

/**
 * Comprehensive text component command handler
 * Handles all text-related AI commands including typography, content, formatting, and effects
 */
export class TextCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    // Phase 4 responsive patterns MUST come first to have priority over base patterns
    // because patterns like "on tablet center" would otherwise match the base "center" pattern
    this.initializeResponsivePatterns(); // Phase 4: Responsive Text Styling (FIRST for priority)
    this.initializePatterns();
    this.initializeRelativeFontPatterns();
    this.initializeColorPatterns(); // Phase 1: Enhanced Color System
    this.initializeTypographyPatterns(); // Phase 2: Advanced Typography Scale
    this.initializeEffectPatterns(); // Phase 3: Text Effects & Visual Enhancements
  }

  /**
   * Initialize command patterns for text operations
   */
  private initializePatterns(): void {
    // ========================================================================
    // Phase 5.1: Enhanced Quoted Content Patterns
    // IMPORTANT: These patterns must come BEFORE simpler content patterns
    // to correctly handle quoted content with escapes
    // ========================================================================

    // Double-quoted content with escape handling: "Say \"Hello\""
    this.commandPatterns.set(
      /^"((?:[^"\\]|\\.)*)"\s*$/,
      (match) => {
        const content = match[1]
          .replace(/\\"/g, '"')
          .replace(/\\\\/g, '\\');
        return { properties: { content } };
      }
    );

    // Single-quoted content with escape handling: 'It\'s working'
    this.commandPatterns.set(
      /^'((?:[^'\\]|\\.)*)'\s*$/,
      (match) => {
        const content = match[1]
          .replace(/\\'/g, "'")
          .replace(/\\\\/g, '\\');
        return { properties: { content } };
      }
    );

    // Backtick content (template literal style): `Code snippet`
    this.commandPatterns.set(
      /^`([^`]*)`\s*$/,
      (match) => {
        return { properties: { content: match[1] } };
      }
    );

    // Explicit content prefix: content: "text" or content = "text" (with double quotes)
    this.commandPatterns.set(
      /^(?:set\s+)?content\s*[:=]\s*"((?:[^"\\]|\\.)*)"\s*$/i,
      (match) => {
        const content = match[1].replace(/\\"/g, '"');
        return { properties: { content } };
      }
    );

    // Explicit content prefix with single quotes: content: 'text' or content = 'text'
    this.commandPatterns.set(
      /^(?:set\s+)?content\s*[:=]\s*'((?:[^'\\]|\\.)*)'\s*$/i,
      (match) => {
        const content = match[1].replace(/\\'/g, "'");
        return { properties: { content } };
      }
    );

    // Explicit content prefix with backticks: content: `text` or content = `text`
    this.commandPatterns.set(
      /^(?:set\s+)?content\s*[:=]\s*`([^`]*)`\s*$/i,
      (match) => {
        return { properties: { content: match[1] } };
      }
    );

    // Write/type/display with double quotes: write "Welcome!"
    this.commandPatterns.set(
      /^(?:write|type|display|show)\s+"((?:[^"\\]|\\.)*)"\s*$/i,
      (match) => {
        const content = match[1].replace(/\\"/g, '"');
        return { properties: { content } };
      }
    );

    // Write/type/display with single quotes: write 'Welcome!'
    this.commandPatterns.set(
      /^(?:write|type|display|show)\s+'((?:[^'\\]|\\.)*)'\s*$/i,
      (match) => {
        const content = match[1].replace(/\\'/g, "'");
        return { properties: { content } };
      }
    );

    // Write/type/display with backticks: write `Welcome!`
    this.commandPatterns.set(
      /^(?:write|type|display|show)\s+`([^`]*)`\s*$/i,
      (match) => {
        return { properties: { content: match[1] } };
      }
    );

    // Set text to with various quote styles (double quotes)
    this.commandPatterns.set(
      /^set\s+(?:text|content)\s+to\s+"((?:[^"\\]|\\.)*)"\s*$/i,
      (match) => {
        const content = match[1].replace(/\\"/g, '"');
        return { properties: { content } };
      }
    );

    // Set text to with single quotes
    this.commandPatterns.set(
      /^set\s+(?:text|content)\s+to\s+'((?:[^'\\]|\\.)*)'\s*$/i,
      (match) => {
        const content = match[1].replace(/\\'/g, "'");
        return { properties: { content } };
      }
    );

    // Set text to with backticks
    this.commandPatterns.set(
      /^set\s+(?:text|content)\s+to\s+`([^`]*)`\s*$/i,
      (match) => {
        return { properties: { content: match[1] } };
      }
    );

    // ========================================================================
    // End Phase 5.1 Enhanced Quoted Content Patterns
    // ========================================================================

    // Direct content patterns - handle content without quotes for simple changes
    // (These come AFTER quoted patterns to avoid capturing quotes as content)
    this.commandPatterns.set(
      /^change (?:the )?content to (.+)$/i,
      (match) => ({ properties: { content: match[1] } })
    );

    // Simple content: pattern - only matches when NOT using quotes
    // Pattern: matches "content:" followed by content that doesn't start with quotes
    this.commandPatterns.set(
      /^content:\s*([^"'`].*)$/i,
      (match) => ({ properties: { content: match[1] } })
    );

    // ========================================================================
    // Legacy simple quote patterns (for backward compatibility with non-escaped quotes)
    // These are less strict and only match simple cases without escapes
    // ========================================================================

    // Simple double-quoted (no escapes) - kept for backward compatibility
    // This pattern is less strict than the Phase 5 pattern above
    this.commandPatterns.set(
      /^"([^"]+)"$/,
      (match) => ({ properties: { content: match[1] } })
    );

    // Simple single-quoted (no escapes) - kept for backward compatibility
    this.commandPatterns.set(
      /^'([^']+)'$/,
      (match) => ({ properties: { content: match[1] } })
    );

    // Font size patterns
    this.commandPatterns.set(
      /(?:set |make |change )?(?:font |text )?size (?:to |= )?(\d+)(?:px)?/i,
      (match) => ({ properties: { fontSize: parseInt(match[1]) } })
    );

    // Simple size pattern for complex commands
    this.commandPatterns.set(
      /^size (\d+)$/i,
      (match) => ({ properties: { fontSize: parseInt(match[1]) } })
    );

    // NEW: Heading shortcuts - must come BEFORE size patterns to avoid conflicts
    this.commandPatterns.set(
      /^h([1-6])$/i,
      (match) => {
        const sizes: Record<string, number> = {
          '1': 32, '2': 28, '3': 24, '4': 20, '5': 18, '6': 16
        };
        return {
          properties: {
            fontSize: sizes[match[1]] || 16,
            fontWeight: 'bold'
          }
        };
      }
    );

    this.commandPatterns.set(
      /make (?:text |font )?(?:bigger|larger)/i,
      (_, component) => {
        const currentSize = asNumber(component.properties?.fontSize) ?? 16;
        return { properties: { fontSize: Math.min(currentSize + 4, 72) } };
      }
    );

    this.commandPatterns.set(
      /make (?:text |font )?(?:smaller|tiny)/i,
      (_, component) => {
        const currentSize = asNumber(component.properties?.fontSize) ?? 16;
        return { properties: { fontSize: Math.max(currentSize - 4, 8) } };
      }
    );

    // Text content patterns - handle both double and single quotes
    this.commandPatterns.set(
      /(?:set |change )?(?:text|content) (?:to |= )?["']([^"']+)["']/i,
      (match) => ({ properties: { content: match[1] } })
    );

    this.commandPatterns.set(
      /(?:write|type|display) ["']([^"']+)["']/i,
      (match) => ({ properties: { content: match[1] } })
    );

    // Additional patterns for single quotes (JavaScript regex needs special handling)
    this.commandPatterns.set(
      /(?:write|type|display) '([^']+)'/,
      (match) => ({ properties: { content: match[1] } })
    );

    this.commandPatterns.set(
      /(?:write|type|display) "([^"]+)"/,
      (match) => ({ properties: { content: match[1] } })
    );

    // Text alignment patterns
    this.commandPatterns.set(
      /(?:align |make )?(?:text )?(?:center|centered)/i,
      () => ({ properties: { textAlign: 'center' } })
    );

    this.commandPatterns.set(
      /(?:align |make )?(?:text )?(?:left|start)/i,
      () => ({ properties: { textAlign: 'left' } })
    );

    this.commandPatterns.set(
      /(?:align |make )?(?:text )?(?:right|end)/i,
      () => ({ properties: { textAlign: 'right' } })
    );

    this.commandPatterns.set(
      /(?:justify|justified)(?:\s+text)?/i,
      () => ({ properties: { textAlign: 'justify' } })
    );

    // Font weight patterns - also handle "bold" standalone
    // NOTE: Use word boundaries \b before AND after 'bold' to prevent matching 'bolder', 'demibold', 'extrabold' etc.
    // Remove bold patterns - MUST come before the generic bold pattern
    this.commandPatterns.set(
      /^(?:remove|no|clear|delete)\s+bold$/i,
      () => ({ properties: { fontWeight: 'normal' } })
    );

    this.commandPatterns.set(
      /^(?:not|un)\s*bold$/i,
      () => ({ properties: { fontWeight: 'normal' } })
    );

    this.commandPatterns.set(
      /(?:make |set )?(?:text |font )?(?:normal|regular|unbold)/i,
      () => ({ properties: { fontWeight: 'normal' } })
    );

    // Bold pattern - must come after remove patterns
    this.commandPatterns.set(
      /^(?:make\s+)?(?:set\s+)?(?:text\s+)?(?:font\s+)?\bbold\b$/i,
      () => ({ properties: { fontWeight: 'bold' } })
    );

    // NEW: Single word style commands
    this.commandPatterns.set(
      /^italic$/i,
      () => ({ properties: { fontStyle: 'italic' } })
    );

    this.commandPatterns.set(
      /^underline$/i,
      () => ({ properties: { textDecoration: 'underline' } })
    );

    this.commandPatterns.set(
      /^uppercase$/i,
      () => ({ properties: { textTransform: 'uppercase' } })
    );

    this.commandPatterns.set(
      /^lowercase$/i,
      () => ({ properties: { textTransform: 'lowercase' } })
    );

    this.commandPatterns.set(
      /^capitalize$/i,
      () => ({ properties: { textTransform: 'capitalize' } })
    );

    this.commandPatterns.set(
      /(?:set |make )?font weight (?:to |= )?(\d{3})/i,
      (match) => ({ properties: { fontWeight: match[1] } })
    );

    // Font style patterns
    // Remove italic patterns - MUST come before the generic italic pattern
    this.commandPatterns.set(
      /^(?:remove|no|clear|delete)\s+italic$/i,
      () => ({ properties: { fontStyle: 'normal' } })
    );

    this.commandPatterns.set(
      /^(?:not|un)\s*italic$/i,
      () => ({ properties: { fontStyle: 'normal' } })
    );

    this.commandPatterns.set(
      /(?:make |set )?(?:text |font )?(?:normal|unitalic|straight)/i,
      () => ({ properties: { fontStyle: 'normal' } })
    );

    this.commandPatterns.set(
      /(?:make |set )?(?:text |font )?italic/i,
      () => ({ properties: { fontStyle: 'italic' } })
    );

    // Text decoration patterns
    // Anchored patterns - Phase 3 handles more complex decorations (wavy, dashed, colored, thick/thin)
    this.commandPatterns.set(
      /^(?:add |make )?underline$/i,
      () => ({ properties: { textDecoration: 'underline' } })
    );

    // Strikethrough - Phase 3 handles colored and styled variants
    this.commandPatterns.set(
      /^(?:add |make )?(?:line-through|strikethrough|strike)$/i,
      () => ({ style: { textDecorationLine: 'line-through' } })
    );

    // Remove underline: "remove underline", "no underline"
    // Smart removal - preserves other decorations like overline or strikethrough
    this.commandPatterns.set(
      /^(?:remove|no|clear|delete)\s+underline$/i,
      (_match, component) => {
        // Get latest component state from store for accurate decoration value
        const latestStyle = getLatestComponentStyle(component);
        const currentDecoration = latestStyle.textDecorationLine || latestStyle.textDecoration || '';
        // Remove 'underline' from the decoration string, keeping others
        const remaining = currentDecoration
          .split(/\s+/)
          .filter(d => d && d !== 'underline' && d !== 'none')
          .join(' ');
        // Clear both style and properties to ensure decoration is removed
        return {
          style: { textDecorationLine: remaining || 'none' },
          properties: { textDecoration: remaining || 'none' }
        };
      }
    );

    // Text transform patterns
    this.commandPatterns.set(
      /(?:make |set )?(?:text )?uppercase/i,
      () => ({ properties: { textTransform: 'uppercase' } })
    );

    this.commandPatterns.set(
      /(?:make |set )?(?:text )?lowercase/i,
      () => ({ properties: { textTransform: 'lowercase' } })
    );

    this.commandPatterns.set(
      /(?:make |set )?(?:text )?capitalize/i,
      () => ({ properties: { textTransform: 'capitalize' } })
    );

    // Color patterns - Enhanced in Phase 1 (basic patterns, detailed patterns in initializeColorPatterns)
    this.commandPatterns.set(
      /(?:set |make |change )?(?:text )?color (?:to |= )?(.+)/i,
      (match) => {
        const color = this.parseEnhancedColorValue(match[1]);
        return color ? { style: { color } } : null;
      }
    );

    // Simple color pattern for complex commands - now supports 50+ colors
    const extendedColorPattern = EXTENDED_CSS_COLORS.join('|');
    this.commandPatterns.set(
      new RegExp(`^(${extendedColorPattern})$`, 'i'),
      (match) => {
        return { style: { color: match[1].toLowerCase() } };
      }
    );

    this.commandPatterns.set(
      /(?:set |make |change )?background (?:color )?(?:to |= )?(.+)/i,
      (match) => {
        const color = this.parseEnhancedColorValue(match[1]);
        return color ? { style: { backgroundColor: color } } : null;
      }
    );

    // Line height patterns
    this.commandPatterns.set(
      /(?:set |make )?line height (?:to |= )?(\d+(?:\.\d+)?)/i,
      (match) => ({ properties: { lineHeight: parseFloat(match[1]) } })
    );

    this.commandPatterns.set(
      /(?:increase|more) (?:line )?spacing/i,
      (_, component) => {
        const current = asNumber(component.properties?.lineHeight) ?? 1.5;
        return { properties: { lineHeight: Math.min(current + 0.2, 3) } };
      }
    );

    this.commandPatterns.set(
      /(?:decrease|less|tight) (?:line )?spacing/i,
      (_, component) => {
        const current = asNumber(component.properties?.lineHeight) ?? 1.5;
        return { properties: { lineHeight: Math.max(current - 0.2, 1) } };
      }
    );

    // Letter spacing patterns
    this.commandPatterns.set(
      /(?:set |make )?letter spacing (?:to |= )?(\d+(?:\.\d+)?)/i,
      (match) => ({ properties: { letterSpacing: `${match[1]}px` } })
    );

    // Font family patterns
    this.commandPatterns.set(
      /(?:set |use |change )?font (?:family )?(?:to |= )?["']([^"']+)["']/i,
      (match) => ({ properties: { fontFamily: match[1] } })
    );

    this.commandPatterns.set(
      /use (?:arial|helvetica|times|georgia|courier|verdana|tahoma) font/i,
      (match) => {
        const fontMap: Record<string, string> = {
          'arial': 'Arial, sans-serif',
          'helvetica': 'Helvetica, sans-serif',
          'times': '"Times New Roman", serif',
          'georgia': 'Georgia, serif',
          'courier': '"Courier New", monospace',
          'verdana': 'Verdana, sans-serif',
          'tahoma': 'Tahoma, sans-serif'
        };
        const font = match[0].toLowerCase().match(/(arial|helvetica|times|georgia|courier|verdana|tahoma)/)?.[1];
        return font ? { properties: { fontFamily: fontMap[font] } } : null;
      }
    );

    // Text shadow patterns - NOTE: Phase 3 has more comprehensive shadow patterns in initializeEffectPatterns()
    // Removed generic shadow patterns to avoid conflicts with Phase 3 shadow presets (xs, sm, md, lg, xl, etc.)

    // Opacity patterns
    this.commandPatterns.set(
      /(?:set |make )?(?:text )?opacity (?:to |= )?(\d+(?:\.\d+)?)/i,
      (match) => ({ style: { opacity: parseFloat(match[1]) } })
    );

    // Padding patterns
    this.commandPatterns.set(
      /(?:set |add )?padding (?:to |= )?(\d+)(?:px)?/i,
      (match) => ({ style: { padding: `${match[1]}px` } })
    );

    // Multi-property commands
    this.commandPatterns.set(
      /make (?:it )?(?:a )?(?:heading|header|title)/i,
      () => ({
        properties: {
          fontSize: 24,
          fontWeight: 'bold',
          lineHeight: 1.2
        }
      })
    );

    this.commandPatterns.set(
      /make (?:it )?(?:a )?(?:subtitle|subheading)/i,
      () => ({
        properties: {
          fontSize: 18,
          fontWeight: '600',
          lineHeight: 1.3
        }
      })
    );

    this.commandPatterns.set(
      /make (?:it )?(?:a )?(?:paragraph|body text)/i,
      () => ({
        properties: {
          fontSize: 16,
          fontWeight: 'normal',
          lineHeight: 1.5
        }
      })
    );

    // Heading shortcuts: h1, h2, h3 (HTML-style heading levels)
    // Note: All alternatives need ^ anchor to prevent matching "h1" inside "fluid h1" etc.
    this.commandPatterns.set(
      /^h1$|^(?:make (?:it )?)?(?:an? )?h1(?:\s|$)|^(?:size|change) (?:to )?h1/i,
      () => {
        console.log('[TextCommandHandler] h1 pattern matched! Returning fontSize: 36');
        return {
          properties: {
            fontSize: 36,
            fontWeight: 'bold',
            lineHeight: 1.2
          }
        };
      }
    );

    this.commandPatterns.set(
      /^h2$|^(?:make (?:it )?)?(?:an? )?h2(?:\s|$)|^(?:size|change) (?:to )?h2/i,
      () => ({
        properties: {
          fontSize: 28,
          fontWeight: 'bold',
          lineHeight: 1.25
        }
      })
    );

    this.commandPatterns.set(
      /^h3$|^(?:make (?:it )?)?(?:an? )?h3(?:\s|$)|^(?:size|change) (?:to )?h3/i,
      () => ({
        properties: {
          fontSize: 22,
          fontWeight: '600',
          lineHeight: 1.3
        }
      })
    );

    this.commandPatterns.set(
      /^h4$|^(?:make (?:it )?)?(?:an? )?h4(?:\s|$)|^(?:size|change) (?:to )?h4/i,
      () => ({
        properties: {
          fontSize: 18,
          fontWeight: '600',
          lineHeight: 1.35
        }
      })
    );

    this.commandPatterns.set(
      /^h5$|^(?:make (?:it )?)?(?:an? )?h5(?:\s|$)|^(?:size|change) (?:to )?h5/i,
      () => ({
        properties: {
          fontSize: 16,
          fontWeight: '600',
          lineHeight: 1.4
        }
      })
    );

    this.commandPatterns.set(
      /^h6$|^(?:make (?:it )?)?(?:an? )?h6(?:\s|$)|^(?:size|change) (?:to )?h6/i,
      () => ({
        properties: {
          fontSize: 14,
          fontWeight: '600',
          lineHeight: 1.4
        }
      })
    );

    // Clear content
    this.commandPatterns.set(
      /(?:clear|empty|remove) (?:text|content)/i,
      () => ({ properties: { content: '' } })
    );
  }

  /**
   * Phase 2.2: Initialize relative font modification patterns
   * These patterns calculate font size changes based on current component values
   */
  private initializeRelativeFontPatterns(): void {
    // ========================================================================
    // RELATIVE FONT SIZE MODIFIERS
    // ========================================================================

    // "bigger text", "larger text", "bigger font", "larger font"
    this.commandPatterns.set(
      /^(?:bigger|larger)\s*(?:text|font)$/i,
      (_, component) => {
        const currentSize = parseComponentFontSize(component);
        const newSize = clampFontSize(currentSize + 4);
        return { properties: { fontSize: newSize } };
      }
    );

    // "smaller text", "smaller font", "tinier text"
    this.commandPatterns.set(
      /^(?:smaller|tinier)\s*(?:text|font)$/i,
      (_, component) => {
        const currentSize = parseComponentFontSize(component);
        const newSize = clampFontSize(currentSize - 2);
        return { properties: { fontSize: newSize } };
      }
    );

    // "much bigger text", "way larger font"
    this.commandPatterns.set(
      /^(?:much|way|a\s*lot)\s*(?:bigger|larger)\s*(?:text|font)$/i,
      (_, component) => {
        const currentSize = parseComponentFontSize(component);
        const newSize = clampFontSize(currentSize + 8);
        return { properties: { fontSize: newSize } };
      }
    );

    // "much smaller text", "way smaller font"
    this.commandPatterns.set(
      /^(?:much|way|a\s*lot)\s*(?:smaller|tinier)\s*(?:text|font)$/i,
      (_, component) => {
        const currentSize = parseComponentFontSize(component);
        const newSize = clampFontSize(currentSize - 6);
        return { properties: { fontSize: newSize } };
      }
    );

    // "slightly bigger text", "a bit larger font"
    this.commandPatterns.set(
      /^(?:slightly|a\s*(?:bit|little))\s*(?:bigger|larger)\s*(?:text|font)$/i,
      (_, component) => {
        const currentSize = parseComponentFontSize(component);
        const newSize = clampFontSize(currentSize + 2);
        return { properties: { fontSize: newSize } };
      }
    );

    // "slightly smaller text", "a bit smaller font"
    this.commandPatterns.set(
      /^(?:slightly|a\s*(?:bit|little))\s*(?:smaller|tinier)\s*(?:text|font)$/i,
      (_, component) => {
        const currentSize = parseComponentFontSize(component);
        const newSize = clampFontSize(currentSize - 1);
        return { properties: { fontSize: newSize } };
      }
    );

    // ========================================================================
    // PERCENTAGE-BASED FONT MODIFIERS
    // ========================================================================

    // "50% bigger font", "25% larger text"
    this.commandPatterns.set(
      /^(\d+)%\s*(?:bigger|larger)\s*(?:text|font)$/i,
      (match, component) => {
        const percent = parseInt(match[1]);
        const currentSize = parseComponentFontSize(component);
        const newSize = clampFontSize(currentSize * (1 + percent / 100));
        return { properties: { fontSize: newSize } };
      }
    );

    // "50% smaller font", "25% tinier text"
    this.commandPatterns.set(
      /^(\d+)%\s*(?:smaller|tinier)\s*(?:text|font)$/i,
      (match, component) => {
        const percent = parseInt(match[1]);
        const currentSize = parseComponentFontSize(component);
        const newSize = clampFontSize(currentSize * (1 - percent / 100));
        return { properties: { fontSize: newSize } };
      }
    );

    // ========================================================================
    // MULTIPLIER-BASED FONT MODIFIERS
    // ========================================================================

    // "double the font size", "double text size"
    this.commandPatterns.set(
      /^double\s*(?:the\s*)?(?:text|font)\s*size$/i,
      (_, component) => {
        const currentSize = parseComponentFontSize(component);
        const newSize = clampFontSize(currentSize * 2);
        return { properties: { fontSize: newSize } };
      }
    );

    // "half the font size", "half text size"
    this.commandPatterns.set(
      /^half\s*(?:the\s*)?(?:text|font)\s*size$/i,
      (_, component) => {
        const currentSize = parseComponentFontSize(component);
        const newSize = clampFontSize(currentSize / 2);
        return { properties: { fontSize: newSize } };
      }
    );

    // ========================================================================
    // INCREMENTAL FONT MODIFIERS (px-based)
    // ========================================================================

    // "increase font by 4", "increase text size by 4px"
    this.commandPatterns.set(
      /^increase\s*(?:the\s*)?(?:text|font)\s*(?:size\s*)?(?:by\s*)?(\d+)\s*(?:px)?$/i,
      (match, component) => {
        const amount = parseInt(match[1]);
        const currentSize = parseComponentFontSize(component);
        const newSize = clampFontSize(currentSize + amount);
        return { properties: { fontSize: newSize } };
      }
    );

    // "decrease font by 4", "reduce text size by 4px"
    this.commandPatterns.set(
      /^(?:decrease|reduce)\s*(?:the\s*)?(?:text|font)\s*(?:size\s*)?(?:by\s*)?(\d+)\s*(?:px)?$/i,
      (match, component) => {
        const amount = parseInt(match[1]);
        const currentSize = parseComponentFontSize(component);
        const newSize = clampFontSize(currentSize - amount);
        return { properties: { fontSize: newSize } };
      }
    );

    // ========================================================================
    // TYPOGRAPHY SCALE MODIFIERS
    // ========================================================================

    // "one step larger", "one size up"
    this.commandPatterns.set(
      /^one\s*(?:step|size)\s*(?:larger|bigger|up)$/i,
      (_, component) => {
        const currentSize = parseComponentFontSize(component);
        const newSize = getNextTypographySize(currentSize);
        return { properties: { fontSize: newSize } };
      }
    );

    // "one step smaller", "one size down"
    this.commandPatterns.set(
      /^one\s*(?:step|size)\s*(?:smaller|tinier|down)$/i,
      (_, component) => {
        const currentSize = parseComponentFontSize(component);
        const newSize = getPreviousTypographySize(currentSize);
        return { properties: { fontSize: newSize } };
      }
    );

    // ========================================================================
    // LINE HEIGHT RELATIVE MODIFIERS
    // ========================================================================

    // "tighter line spacing", "less line height"
    this.commandPatterns.set(
      /^(?:tighter|less)\s*(?:line\s*)?(?:spacing|height)$/i,
      (_, component) => {
        const currentLineHeight = asNumber(component.properties?.lineHeight) ?? 1.5;
        const newLineHeight = Math.max(1, currentLineHeight - 0.15);
        return { properties: { lineHeight: parseFloat(newLineHeight.toFixed(2)) } };
      }
    );

    // "looser line spacing", "more line height"
    this.commandPatterns.set(
      /^(?:looser|more)\s*(?:line\s*)?(?:spacing|height)$/i,
      (_, component) => {
        const currentLineHeight = asNumber(component.properties?.lineHeight) ?? 1.5;
        const newLineHeight = Math.min(3, currentLineHeight + 0.15);
        return { properties: { lineHeight: parseFloat(newLineHeight.toFixed(2)) } };
      }
    );

    // ========================================================================
    // LETTER SPACING RELATIVE MODIFIERS
    // ========================================================================

    // "tighter letter spacing", "less letter spacing"
    this.commandPatterns.set(
      /^(?:tighter|less)\s*letter\s*spacing$/i,
      (_, component) => {
        const current = parseFloat(String(component.properties?.letterSpacing || '0'));
        const newSpacing = Math.max(-2, current - 0.5);
        return { properties: { letterSpacing: `${newSpacing}px` } };
      }
    );

    // "looser letter spacing", "more letter spacing"
    this.commandPatterns.set(
      /^(?:looser|more)\s*letter\s*spacing$/i,
      (_, component) => {
        const current = parseFloat(String(component.properties?.letterSpacing || '0'));
        const newSpacing = Math.min(10, current + 0.5);
        return { properties: { letterSpacing: `${newSpacing}px` } };
      }
    );

  }

  /**
   * Phase 1: Initialize enhanced color patterns
   * Includes extended CSS colors, brand colors, semantic colors, and brightness modifiers
   */
  private initializeColorPatterns(): void {
    // ========================================================================
    // 1.1 EXTENDED CSS COLOR PATTERNS
    // ========================================================================

    // Color-first syntax: "navy text", "coral text", "indigo text"
    const extendedColorPattern = EXTENDED_CSS_COLORS.join('|');
    this.commandPatterns.set(
      new RegExp(`^(${extendedColorPattern})\\s+(?:text|color)$`, 'i'),
      (match) => {
        return { style: { color: match[1].toLowerCase() } };
      }
    );

    // "make text X" pattern for extended colors
    this.commandPatterns.set(
      new RegExp(`^make\\s+(?:the\\s+)?text\\s+(${extendedColorPattern})$`, 'i'),
      (match) => {
        return { style: { color: match[1].toLowerCase() } };
      }
    );

    // Hex color shorthand: "#abc", "#aabbcc", "#f00"
    this.commandPatterns.set(
      /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/,
      (match) => {
        let hex = match[1];
        if (hex.length === 3) {
          hex = hex.split('').map(c => c + c).join('');
        }
        return { style: { color: `#${hex}` } };
      }
    );

    // "text #hex" or "color #hex"
    this.commandPatterns.set(
      /^(?:text\s+|color\s+)?#([0-9a-fA-F]{3,6})$/i,
      (match) => {
        let hex = match[1];
        if (hex.length === 3) {
          hex = hex.split('').map(c => c + c).join('');
        }
        return { style: { color: `#${hex}` } };
      }
    );

    // HSL color support: "hsl(120, 50%, 50%)" or "hsl 120 50 50"
    this.commandPatterns.set(
      /^hsl\s*\(?\s*(\d+)\s*,?\s*(\d+)%?\s*,?\s*(\d+)%?\s*\)?$/i,
      (match) => {
        const h = parseInt(match[1]);
        const s = parseInt(match[2]);
        const l = parseInt(match[3]);
        return { style: { color: `hsl(${h}, ${s}%, ${l}%)` } };
      }
    );

    // ========================================================================
    // 1.2 TERRAMATRIX BRAND COLOR PATTERNS
    // ========================================================================

    // Build brand color pattern (handles multi-word names like "forest green")
    const brandColorNames = Object.keys(BRAND_TEXT_COLORS);
    const brandPatternParts = brandColorNames.map(name => name.replace(/\s+/g, '\\s+'));
    const brandPattern = brandPatternParts.join('|');

    // Direct brand color: "forest green", "sage", "primary", etc.
    this.commandPatterns.set(
      new RegExp(`^(${brandPattern})$`, 'i'),
      (match) => {
        const colorKey = match[1].toLowerCase().replace(/\s+/g, ' ');
        const hexColor = BRAND_TEXT_COLORS[colorKey];
        if (hexColor) {
          return { style: { color: hexColor } };
        }
        return null;
      }
    );

    // "X text" where X is a brand color: "forest green text", "primary text"
    this.commandPatterns.set(
      new RegExp(`^(${brandPattern})\\s+(?:text|color)$`, 'i'),
      (match) => {
        const colorKey = match[1].toLowerCase().replace(/\s+/g, ' ');
        const hexColor = BRAND_TEXT_COLORS[colorKey];
        if (hexColor) {
          return { style: { color: hexColor } };
        }
        return null;
      }
    );

    // "make text X" where X is brand color
    this.commandPatterns.set(
      new RegExp(`^make\\s+(?:the\\s+)?text\\s+(${brandPattern})$`, 'i'),
      (match) => {
        const colorKey = match[1].toLowerCase().replace(/\s+/g, ' ');
        const hexColor = BRAND_TEXT_COLORS[colorKey];
        if (hexColor) {
          return { style: { color: hexColor } };
        }
        return null;
      }
    );

    // "use X color" where X is brand color
    this.commandPatterns.set(
      new RegExp(`^use\\s+(${brandPattern})(?:\\s+color)?$`, 'i'),
      (match) => {
        const colorKey = match[1].toLowerCase().replace(/\s+/g, ' ');
        const hexColor = BRAND_TEXT_COLORS[colorKey];
        if (hexColor) {
          return { style: { color: hexColor } };
        }
        return null;
      }
    );

    // ========================================================================
    // 1.3 COLOR BRIGHTNESS MODIFIERS
    // ========================================================================

    // "darker text", "darker color"
    this.commandPatterns.set(
      /^darker(?:\s+(?:text|color))?$/i,
      (_, component) => {
        const currentColor = asString(component.style?.color) ?? asString(component.properties?.color) ?? '#000000';
        const adjustedColor = adjustColorBrightness(currentColor, -0.2);
        return adjustedColor ? { style: { color: adjustedColor } } : null;
      }
    );

    // "lighter text", "lighter color"
    this.commandPatterns.set(
      /^lighter(?:\s+(?:text|color))?$/i,
      (_, component) => {
        const currentColor = asString(component.style?.color) ?? asString(component.properties?.color) ?? '#000000';
        const adjustedColor = adjustColorBrightness(currentColor, 0.2);
        return adjustedColor ? { style: { color: adjustedColor } } : null;
      }
    );

    // "much darker", "way darker"
    this.commandPatterns.set(
      /^(?:much|way|a\s*lot)\s+darker(?:\s+(?:text|color))?$/i,
      (_, component) => {
        const currentColor = asString(component.style?.color) ?? asString(component.properties?.color) ?? '#000000';
        const adjustedColor = adjustColorBrightness(currentColor, -0.4);
        return adjustedColor ? { style: { color: adjustedColor } } : null;
      }
    );

    // "much lighter", "way lighter"
    this.commandPatterns.set(
      /^(?:much|way|a\s*lot)\s+lighter(?:\s+(?:text|color))?$/i,
      (_, component) => {
        const currentColor = asString(component.style?.color) ?? asString(component.properties?.color) ?? '#000000';
        const adjustedColor = adjustColorBrightness(currentColor, 0.4);
        return adjustedColor ? { style: { color: adjustedColor } } : null;
      }
    );

    // "slightly darker", "a bit darker"
    this.commandPatterns.set(
      /^(?:slightly|a\s*(?:bit|little))\s+darker(?:\s+(?:text|color))?$/i,
      (_, component) => {
        const currentColor = asString(component.style?.color) ?? asString(component.properties?.color) ?? '#000000';
        const adjustedColor = adjustColorBrightness(currentColor, -0.1);
        return adjustedColor ? { style: { color: adjustedColor } } : null;
      }
    );

    // "slightly lighter", "a bit lighter"
    this.commandPatterns.set(
      /^(?:slightly|a\s*(?:bit|little))\s+lighter(?:\s+(?:text|color))?$/i,
      (_, component) => {
        const currentColor = asString(component.style?.color) ?? asString(component.properties?.color) ?? '#000000';
        const adjustedColor = adjustColorBrightness(currentColor, 0.1);
        return adjustedColor ? { style: { color: adjustedColor } } : null;
      }
    );

    // "make it darker/lighter"
    this.commandPatterns.set(
      /^make\s+(?:it\s+|the\s+(?:text\s+)?)?darker$/i,
      (_, component) => {
        const currentColor = asString(component.style?.color) ?? asString(component.properties?.color) ?? '#000000';
        const adjustedColor = adjustColorBrightness(currentColor, -0.2);
        return adjustedColor ? { style: { color: adjustedColor } } : null;
      }
    );

    this.commandPatterns.set(
      /^make\s+(?:it\s+|the\s+(?:text\s+)?)?lighter$/i,
      (_, component) => {
        const currentColor = asString(component.style?.color) ?? asString(component.properties?.color) ?? '#000000';
        const adjustedColor = adjustColorBrightness(currentColor, 0.2);
        return adjustedColor ? { style: { color: adjustedColor } } : null;
      }
    );

    // ========================================================================
    // 1.4 SEMANTIC COLOR PATTERNS
    // ========================================================================

    // Build semantic color pattern
    const semanticColorNames = Object.keys(SEMANTIC_COLORS);
    const semanticPattern = semanticColorNames.join('|');

    // Direct semantic color: "success", "error", "danger", "warning", "info"
    this.commandPatterns.set(
      new RegExp(`^(${semanticPattern})$`, 'i'),
      (match) => {
        const colorKey = match[1].toLowerCase();
        const hexColor = SEMANTIC_COLORS[colorKey];
        if (hexColor) {
          return { style: { color: hexColor } };
        }
        return null;
      }
    );

    // "success text", "error text", "danger color"
    this.commandPatterns.set(
      new RegExp(`^(${semanticPattern})\\s+(?:text|color)$`, 'i'),
      (match) => {
        const colorKey = match[1].toLowerCase();
        const hexColor = SEMANTIC_COLORS[colorKey];
        if (hexColor) {
          return { style: { color: hexColor } };
        }
        return null;
      }
    );

    // "make it X color" where X is semantic
    this.commandPatterns.set(
      new RegExp(`^make\\s+(?:it\\s+|the\\s+text\\s+)?(${semanticPattern})(?:\\s+color)?$`, 'i'),
      (match) => {
        const colorKey = match[1].toLowerCase();
        const hexColor = SEMANTIC_COLORS[colorKey];
        if (hexColor) {
          return { style: { color: hexColor } };
        }
        return null;
      }
    );

    // "use success/error/etc styling"
    this.commandPatterns.set(
      new RegExp(`^use\\s+(${semanticPattern})\\s+(?:color|styling?)$`, 'i'),
      (match) => {
        const colorKey = match[1].toLowerCase();
        const hexColor = SEMANTIC_COLORS[colorKey];
        if (hexColor) {
          return { style: { color: hexColor } };
        }
        return null;
      }
    );

    // Specific semantic shortcuts
    this.commandPatterns.set(
      /^(?:mark\s+as\s+)?(?:success(?:ful)?|valid|positive)$/i,
      () => {
        return { style: { color: SEMANTIC_COLORS['success'] } };
      }
    );

    this.commandPatterns.set(
      /^(?:mark\s+as\s+)?(?:error|danger(?:ous)?|invalid|negative)$/i,
      () => {
        return { style: { color: SEMANTIC_COLORS['error'] } };
      }
    );

    logger.debug("TextCommandHandler color patterns initialized", {
      extendedCssColors: EXTENDED_CSS_COLORS.length,
      brandColors: Object.keys(BRAND_TEXT_COLORS).length,
      semanticColors: Object.keys(SEMANTIC_COLORS).length,
    });
  }

  /**
   * Phase 2: Initialize advanced typography patterns
   * Includes font weights, font families, display presets, text presets, and spacing presets
   */
  private initializeTypographyPatterns(): void {
    // ========================================================================
    // 2.1 NUMERIC FONT WEIGHTS (100-900)
    // ========================================================================

    // Named font weights: "thin", "light", "medium", "semibold", "extrabold", "black"
    const weightNames = Object.keys(FONT_WEIGHT_MAP).join('|');
    this.commandPatterns.set(
      new RegExp(`^(?:make\\s+)?(?:text\\s+)?(?:font\\s+)?(${weightNames})$`, 'i'),
      (match) => {
        const weightKey = match[1].toLowerCase();
        const weight = FONT_WEIGHT_MAP[weightKey];
        if (weight) {
          return { properties: { fontWeight: weight } };
        }
        return null;
      }
    );

    // "set weight to thin", "font weight medium"
    this.commandPatterns.set(
      new RegExp(`^(?:set\\s+)?(?:font\\s+)?weight\\s+(?:to\\s+)?(${weightNames})$`, 'i'),
      (match) => {
        const weightKey = match[1].toLowerCase();
        const weight = FONT_WEIGHT_MAP[weightKey];
        if (weight) {
          return { properties: { fontWeight: weight } };
        }
        return null;
      }
    );

    // Numeric weight: "weight 500", "font weight 700", "set weight to 400"
    this.commandPatterns.set(
      /^(?:set\s+)?(?:font\s+)?weight\s+(?:to\s+)?(\d+)$/i,
      (match) => {
        const rawWeight = parseInt(match[1]);
        // Clamp to valid CSS range and round to nearest 100
        const weight = Math.max(100, Math.min(900, Math.round(rawWeight / 100) * 100));
        return { properties: { fontWeight: String(weight) } };
      }
    );

    // Variable font weight (fine control): "weight 450", "font weight 325"
    // For variable fonts that support any weight
    this.commandPatterns.set(
      /^(?:variable\s+)?weight\s+(\d+)$/i,
      (match) => {
        const weight = Math.max(1, Math.min(1000, parseInt(match[1])));
        return { properties: { fontWeight: String(weight) } };
      }
    );

    // Relative weight: "bolder"
    this.commandPatterns.set(
      /^(?:make\s+)?(?:text\s+)?bolder$/i,
      (_, component) => {
        const currentWeight = parseInt(String(component.properties?.fontWeight || '400'));
        const newWeight = Math.min(900, currentWeight + 100);
        return { properties: { fontWeight: String(newWeight) } };
      }
    );

    // Relative weight: "lighter" (for weight, not color)
    // Supports: "weight lighter", "font weight lighter", "font lighter"
    this.commandPatterns.set(
      /^(?:make\s+)?(?:text\s+)?(?:font\s+)?weight\s+lighter$/i,
      (_, component) => {
        const currentWeight = parseInt(String(component.properties?.fontWeight || '400'));
        const newWeight = Math.max(100, currentWeight - 100);
        return { properties: { fontWeight: String(newWeight) } };
      }
    );

    // Relative weight: "font lighter" - alternative phrasing
    this.commandPatterns.set(
      /^(?:make\s+)?(?:text\s+)?font\s+lighter$/i,
      (_, component) => {
        const currentWeight = parseInt(String(component.properties?.fontWeight || '400'));
        const newWeight = Math.max(100, currentWeight - 100);
        return { properties: { fontWeight: String(newWeight) } };
      }
    );

    // Relative weight: "lighter weight" - reversed order
    this.commandPatterns.set(
      /^(?:make\s+)?(?:text\s+)?lighter\s+(?:font\s+)?weight$/i,
      (_, component) => {
        const currentWeight = parseInt(String(component.properties?.fontWeight || '400'));
        const newWeight = Math.max(100, currentWeight - 100);
        return { properties: { fontWeight: String(newWeight) } };
      }
    );

    // Relative weight: "less bold" - natural language for decreasing weight
    this.commandPatterns.set(
      /^(?:make\s+)?(?:it\s+)?(?:text\s+)?less\s+bold$/i,
      (_, component) => {
        const currentWeight = parseInt(String(component.properties?.fontWeight || '400'));
        const newWeight = Math.max(100, currentWeight - 100);
        return { properties: { fontWeight: String(newWeight) } };
      }
    );

    // Relative weight: "not as bold" - alternative phrasing
    this.commandPatterns.set(
      /^(?:make\s+)?(?:it\s+)?(?:text\s+)?not\s+(?:as\s+)?bold$/i,
      (_, component) => {
        const currentWeight = parseInt(String(component.properties?.fontWeight || '400'));
        const newWeight = Math.max(100, currentWeight - 100);
        return { properties: { fontWeight: String(newWeight) } };
      }
    );

    // ========================================================================
    // 2.2 EXTENDED FONT FAMILY SUPPORT
    // ========================================================================

    // Build font family pattern from map keys
    const fontFamilyNames = Object.keys(FONT_FAMILY_MAP).map(name =>
      name.replace(/\s+/g, '\\s*') // Handle "open sans" -> "open\s*sans"
    ).join('|');

    // Direct font family: "serif", "monospace", "arial", "roboto"
    this.commandPatterns.set(
      new RegExp(`^(?:use\\s+)?(?:font\\s+)?(?:family\\s+)?(?:to\\s+)?(${fontFamilyNames})(?:\\s+font)?$`, 'i'),
      (match) => {
        const fontKey = match[1].toLowerCase().replace(/\s+/g, ' ');
        // Handle variants like "opensans" and "open sans"
        const normalizedKey = fontKey.replace(/\s/g, '');
        const fontFamily = FONT_FAMILY_MAP[fontKey] || FONT_FAMILY_MAP[normalizedKey];
        if (fontFamily) {
          return { properties: { fontFamily } };
        }
        return null;
      }
    );

    // "set font to X", "use X font", "font family X"
    this.commandPatterns.set(
      new RegExp(`^(?:set\\s+)?(?:font\\s+)?(?:family\\s+)?(?:to\\s+)?(${fontFamilyNames})$`, 'i'),
      (match) => {
        const fontKey = match[1].toLowerCase().replace(/\s+/g, ' ');
        const normalizedKey = fontKey.replace(/\s/g, '');
        const fontFamily = FONT_FAMILY_MAP[fontKey] || FONT_FAMILY_MAP[normalizedKey];
        if (fontFamily) {
          return { properties: { fontFamily } };
        }
        return null;
      }
    );

    // "use system font", "system ui font"
    this.commandPatterns.set(
      /^(?:use\s+)?system(?:\s*ui)?(?:\s+font)?$/i,
      () => {
        return { properties: { fontFamily: FONT_FAMILY_MAP['system'] } };
      }
    );

    // "code font", "use code font", "monospace font"
    this.commandPatterns.set(
      /^(?:use\s+)?(?:code|mono(?:space)?)\s*font$/i,
      () => {
        return { properties: { fontFamily: FONT_FAMILY_MAP['monospace'] } };
      }
    );

    // Custom font name (quoted): 'use "Custom Font Name"', "font 'My Font'"
    this.commandPatterns.set(
      /^(?:use\s+|set\s+)?(?:font\s+)?(?:family\s+)?(?:to\s+)?["']([^"']+)["']$/i,
      (match) => {
        const customFont = match[1];
        return { properties: { fontFamily: `"${customFont}", sans-serif` } };
      }
    );

    // ========================================================================
    // 2.3 ENHANCED HEADING & TEXT PRESETS
    // ========================================================================

    // Display sizes: "display xl", "display 2xl", "display-lg"
    this.commandPatterns.set(
      /^(?:make\s+)?(?:it\s+)?display[\s-]?(2xl|xl|lg|md|sm)$/i,
      (match) => {
        const size = match[1].toLowerCase();
        const presetKey = `display-${size}`;
        const preset = DISPLAY_PRESETS[presetKey];
        if (preset) {
          return { properties: { ...preset } };
        }
        return null;
      }
    );

    // "make it display large" (worded variant)
    this.commandPatterns.set(
      /^(?:make\s+)?(?:it\s+)?display\s+(extra\s*large|large|medium|small)$/i,
      (match) => {
        const sizeMap: Record<string, string> = {
          'extra large': 'display-xl',
          'extralarge': 'display-xl',
          'large': 'display-lg',
          'medium': 'display-md',
          'small': 'display-sm',
        };
        const size = match[1].toLowerCase().replace(/\s+/g, '');
        const presetKey = sizeMap[size] || sizeMap[match[1].toLowerCase()];
        const preset = presetKey ? DISPLAY_PRESETS[presetKey] : null;
        if (preset) {
          return { properties: { ...preset } };
        }
        return null;
      }
    );

    // Semantic text presets: "caption", "overline", "eyebrow", "label", "lead", "blockquote", "code"
    const textPresetNames = Object.keys(TEXT_PRESETS).join('|');
    this.commandPatterns.set(
      new RegExp(`^(?:make\\s+)?(?:it\\s+)?(?:a\\s+)?(${textPresetNames})$`, 'i'),
      (match) => {
        let presetKey = match[1].toLowerCase().replace(/\s+/g, '-');
        // Handle aliases
        if (presetKey === 'inline-code') presetKey = 'code';
        const preset = TEXT_PRESETS[presetKey];
        if (preset) {
          // Separate style properties (color, fontStyle) from regular properties
          const { color, fontStyle, ...properties } = preset;
          const result: TextPresetResult = { properties };
          if (color || fontStyle) {
            result.style = {};
            if (color) result.style.color = color;
            if (fontStyle) result.properties.fontStyle = fontStyle;
          }
          return result;
        }
        return null;
      }
    );

    // "make it a caption", "apply label style", "use overline"
    this.commandPatterns.set(
      new RegExp(`^(?:apply|use)\\s+(${textPresetNames})(?:\\s+style)?$`, 'i'),
      (match) => {
        const presetKey = match[1].toLowerCase().replace(/\s+/g, '-');
        const preset = TEXT_PRESETS[presetKey];
        if (preset) {
          const { color, fontStyle, ...properties } = preset;
          const result: TextPresetResult = { properties };
          if (color || fontStyle) {
            result.style = {};
            if (color) result.style.color = color;
            if (fontStyle) result.properties.fontStyle = fontStyle;
          }
          return result;
        }
        return null;
      }
    );

    // ========================================================================
    // 2.4 LINE HEIGHT & LETTER SPACING PRESETS
    // ========================================================================

    // Line height presets: "tight leading", "loose leading", "snug"
    const lineHeightNames = Object.keys(LINE_HEIGHT_PRESETS).join('|');

    // "tight leading", "loose leading"
    this.commandPatterns.set(
      new RegExp(`^(${lineHeightNames})\\s+leading$`, 'i'),
      (match) => {
        const presetKey = match[1].toLowerCase();
        const value = LINE_HEIGHT_PRESETS[presetKey];
        if (value !== undefined) {
          return { properties: { lineHeight: value } };
        }
        return null;
      }
    );

    // "leading tight", "leading loose"
    this.commandPatterns.set(
      new RegExp(`^leading\\s+(${lineHeightNames})$`, 'i'),
      (match) => {
        const presetKey = match[1].toLowerCase();
        const value = LINE_HEIGHT_PRESETS[presetKey];
        if (value !== undefined) {
          return { properties: { lineHeight: value } };
        }
        return null;
      }
    );

    // Direct preset name for line height: "tight", "loose", "relaxed" (when in context)
    // Note: "tight" and "loose" can be ambiguous, so we use specific patterns
    this.commandPatterns.set(
      /^(?:set\s+)?(?:line\s*height|leading)\s+(?:to\s+)?(none|tight|snug|normal|relaxed|loose)$/i,
      (match) => {
        const presetKey = match[1].toLowerCase();
        const value = LINE_HEIGHT_PRESETS[presetKey];
        if (value !== undefined) {
          return { properties: { lineHeight: value } };
        }
        return null;
      }
    );

    // Letter spacing presets (tracking): "tight tracking", "wide tracking"
    const letterSpacingNames = Object.keys(LETTER_SPACING_PRESETS).join('|');

    // "tight tracking", "wide tracking", "widest tracking"
    this.commandPatterns.set(
      new RegExp(`^(${letterSpacingNames})\\s+tracking$`, 'i'),
      (match) => {
        const presetKey = match[1].toLowerCase();
        const value = LETTER_SPACING_PRESETS[presetKey];
        if (value !== undefined) {
          return { properties: { letterSpacing: value } };
        }
        return null;
      }
    );

    // "tracking tight", "tracking wide"
    this.commandPatterns.set(
      new RegExp(`^tracking\\s+(${letterSpacingNames})$`, 'i'),
      (match) => {
        const presetKey = match[1].toLowerCase();
        const value = LETTER_SPACING_PRESETS[presetKey];
        if (value !== undefined) {
          return { properties: { letterSpacing: value } };
        }
        return null;
      }
    );

    // "set letter spacing to tight", "letter spacing wide"
    this.commandPatterns.set(
      new RegExp(`^(?:set\\s+)?(?:letter\\s*spacing)\\s+(?:to\\s+)?(${letterSpacingNames})$`, 'i'),
      (match) => {
        const presetKey = match[1].toLowerCase();
        const value = LETTER_SPACING_PRESETS[presetKey];
        if (value !== undefined) {
          return { properties: { letterSpacing: value } };
        }
        return null;
      }
    );

    // Single-word presets that set both line height and letter spacing contextually
    // "loose" -> line height 2 (but only as standalone, not "loose tracking")
    this.commandPatterns.set(
      /^(?:make\s+)?(?:it\s+)?(?:text\s+)?(tight|snug|relaxed|loose)$/i,
      (match) => {
        const presetKey = match[1].toLowerCase();
        const lineHeight = LINE_HEIGHT_PRESETS[presetKey];
        if (lineHeight !== undefined) {
          return { properties: { lineHeight } };
        }
        return null;
      }
    );

    logger.debug("TextCommandHandler typography patterns initialized", {
      namedWeights: Object.keys(FONT_WEIGHT_MAP).length,
      fontFamilies: Object.keys(FONT_FAMILY_MAP).length,
      displayPresets: Object.keys(DISPLAY_PRESETS).length,
      textPresets: Object.keys(TEXT_PRESETS).length,
      lineHeightPresets: Object.keys(LINE_HEIGHT_PRESETS).length,
      letterSpacingPresets: Object.keys(LETTER_SPACING_PRESETS).length,
    });
  }

  /**
   * Phase 3: Initialize text effect patterns
   * Includes shadows, glows, decoration enhancements, outlines, and gradients
   */
  private initializeEffectPatterns(): void {
    // ========================================================================
    // 3.1 TEXT SHADOW PRESETS
    // ========================================================================

    // Shadow preset patterns: "shadow sm", "shadow md", "text shadow lg"
    // Preserves existing color if present
    const shadowPresetNames = Object.keys(TEXT_SHADOW_PRESETS).join('|');
    this.commandPatterns.set(
      new RegExp(`^(?:add\\s+)?(?:text\\s+)?shadow\\s+(${shadowPresetNames})$`, 'i'),
      (match, component) => {
        const preset = match[1].toLowerCase();
        const existingColor = extractShadowColor(asString(component?.style?.textShadow));
        const shadowValue = buildTextShadow(preset, existingColor);
        return { style: { textShadow: shadowValue } };
      }
    );

    // Shadow preset patterns (size first): "xs shadow", "sm shadow", "md shadow"
    // Also accepts aliases: "small shadow", "medium shadow", "large shadow"
    // Preserves existing color if present
    const sizeAliases: Record<string, string> = {
      'small': 'sm', 'medium': 'md', 'large': 'lg',
      'extra-small': 'xs', 'extra-large': 'xl',
      'extrasmall': 'xs', 'extralarge': 'xl'
    };
    const extendedSizeNames = `${shadowPresetNames}|small|medium|large|extra-small|extra-large|extrasmall|extralarge`;
    this.commandPatterns.set(
      new RegExp(`^(${extendedSizeNames})\\s+(?:text\\s+)?shadow$`, 'i'),
      (match, component) => {
        let preset = match[1].toLowerCase();
        preset = sizeAliases[preset] || preset; // Convert alias to actual preset name
        const existingColor = extractShadowColor(asString(component?.style?.textShadow));
        const shadowValue = buildTextShadow(preset, existingColor);
        return { style: { textShadow: shadowValue } };
      }
    );

    // Simple shadow: "add shadow", "text shadow" (defaults to md)
    this.commandPatterns.set(
      /^(?:add\s+)?(?:a\s+)?(?:text\s+)?shadow$/i,
      () => {
        return { style: { textShadow: TEXT_SHADOW_PRESETS['md'] } };
      }
    );

    // Soft/subtle shadow: "soft shadow", "subtle shadow"
    this.commandPatterns.set(
      /^(?:add\s+)?(?:a\s+)?(?:soft|subtle|light)\s+(?:text\s+)?shadow$/i,
      () => {
        return { style: { textShadow: TEXT_SHADOW_PRESETS['soft'] } };
      }
    );

    // Hard/strong shadow: "hard shadow", "strong shadow"
    this.commandPatterns.set(
      /^(?:add\s+)?(?:a\s+)?(?:hard|strong|heavy)\s+(?:text\s+)?shadow$/i,
      () => {
        return { style: { textShadow: TEXT_SHADOW_PRESETS['hard'] } };
      }
    );

    // Crisp shadow: "crisp shadow"
    this.commandPatterns.set(
      /^(?:add\s+)?(?:a\s+)?crisp\s+(?:text\s+)?shadow$/i,
      () => {
        return { style: { textShadow: TEXT_SHADOW_PRESETS['crisp'] } };
      }
    );

    // Colored shadows: "blue shadow", "shadow red", "red text shadow"
    // Preserves existing size if present
    const coloredShadowNames = Object.keys(COLORED_SHADOW_MAP).join('|');
    this.commandPatterns.set(
      new RegExp(`^(?:add\\s+)?(?:a\\s+)?(${coloredShadowNames})\\s+(?:text\\s+)?shadow$`, 'i'),
      (match, component) => {
        const color = match[1].toLowerCase();
        const shadowColor = COLORED_SHADOW_MAP[color];
        const shadowValue = buildTextShadowWithColor(asString(component?.style?.textShadow), shadowColor);
        return { style: { textShadow: shadowValue } };
      }
    );

    // "shadow COLOR" variant - preserves existing size
    this.commandPatterns.set(
      new RegExp(`^(?:text\\s+)?shadow\\s+(${coloredShadowNames})$`, 'i'),
      (match, component) => {
        const color = match[1].toLowerCase();
        const shadowColor = COLORED_SHADOW_MAP[color];
        const shadowValue = buildTextShadowWithColor(asString(component?.style?.textShadow), shadowColor);
        return { style: { textShadow: shadowValue } };
      }
    );

    // Combined size + color shadows: "xl red shadow", "lg blue shadow"
    // Size-color-shadow format: "xl red shadow", "sm blue shadow"
    this.commandPatterns.set(
      new RegExp(`^(xs|sm|md|lg|xl)\\s+(${coloredShadowNames})\\s+shadow$`, 'i'),
      (match) => {
        const size = match[1].toLowerCase();
        const color = match[2].toLowerCase();
        const shadowColor = COLORED_SHADOW_MAP[color];
        // Extract offset and blur from preset, replace color
        const preset = TEXT_SHADOW_PRESETS[size];
        const parts = preset.match(/^(\d+px)\s+(\d+px)\s+(\d+px)/);
        if (parts) {
          const shadowValue = `${parts[1]} ${parts[2]} ${parts[3]} ${shadowColor}`;
          return { style: { textShadow: shadowValue } };
        }
        return { style: { textShadow: `2px 2px 4px ${shadowColor}` } };
      }
    );

    // Color-size-shadow format: "red xl shadow", "blue sm shadow"
    this.commandPatterns.set(
      new RegExp(`^(${coloredShadowNames})\\s+(xs|sm|md|lg|xl)\\s+shadow$`, 'i'),
      (match) => {
        const color = match[1].toLowerCase();
        const size = match[2].toLowerCase();
        const shadowColor = COLORED_SHADOW_MAP[color];
        const preset = TEXT_SHADOW_PRESETS[size];
        const parts = preset.match(/^(\d+px)\s+(\d+px)\s+(\d+px)/);
        if (parts) {
          const shadowValue = `${parts[1]} ${parts[2]} ${parts[3]} ${shadowColor}`;
          return { style: { textShadow: shadowValue } };
        }
        return { style: { textShadow: `2px 2px 4px ${shadowColor}` } };
      }
    );

    // Remove shadow: "remove shadow", "no shadow", "clear shadow"
    this.commandPatterns.set(
      /^(?:remove|no|clear|delete)\s+(?:text\s+)?shadow$/i,
      () => {
        return { style: { textShadow: 'none' } };
      }
    );

    // ========================================================================
    // 3.1 GLOW EFFECT PATTERNS
    // ========================================================================

    // Glow presets: "glow", "text glow", "add glow"
    this.commandPatterns.set(
      /^(?:add\s+)?(?:a\s+)?(?:text\s+)?glow$/i,
      () => {
        return { style: { textShadow: GLOW_PRESETS['glow'] } };
      }
    );

    // Soft glow: "soft glow", "subtle glow"
    this.commandPatterns.set(
      /^(?:add\s+)?(?:a\s+)?(?:soft|subtle)\s+(?:text\s+)?glow$/i,
      () => {
        return { style: { textShadow: GLOW_PRESETS['soft-glow'] } };
      }
    );

    // Neon glow: "neon glow", "neon effect", "neon text"
    this.commandPatterns.set(
      /^(?:add\s+)?(?:a\s+)?neon\s+(?:text\s+)?(?:glow|effect)?$/i,
      () => {
        return { style: { textShadow: GLOW_PRESETS['neon'] } };
      }
    );

    this.commandPatterns.set(
      /^(?:make\s+)?(?:it\s+)?neon$/i,
      () => {
        return { style: { textShadow: GLOW_PRESETS['neon'] } };
      }
    );

    // Colored glow: "blue glow", "green glow", "red glow"
    const glowColorNames = ['blue', 'green', 'red', 'purple', 'orange', 'cyan', 'white', 'yellow', 'pink'];
    const glowColorPattern = glowColorNames.join('|');
    this.commandPatterns.set(
      new RegExp(`^(?:add\\s+)?(?:a\\s+)?(${glowColorPattern})\\s+(?:text\\s+)?glow$`, 'i'),
      (match) => {
        const color = match[1].toLowerCase();
        const glowKey = `${color}-glow`;
        return { style: { textShadow: GLOW_PRESETS[glowKey] || GLOW_PRESETS['glow'] } };
      }
    );

    // Remove glow: "remove glow", "no glow"
    this.commandPatterns.set(
      /^(?:remove|no|clear)\s+(?:text\s+)?glow$/i,
      () => {
        return { style: { textShadow: 'none' } };
      }
    );

    // ========================================================================
    // 3.2 TEXT DECORATION ENHANCEMENTS
    // ========================================================================

    // Styled underlines: "dashed underline", "wavy underline", "dotted underline", "double underline"
    // Use longhand properties to avoid React warning about mixing shorthand/longhand
    const decorationStylePattern = DECORATION_STYLES.join('|');
    this.commandPatterns.set(
      new RegExp(`^(?:add\\s+)?(?:a\\s+)?(${decorationStylePattern})\\s+underline$`, 'i'),
      (match) => {
        const style = match[1].toLowerCase();
        return {
          style: { textDecorationLine: 'underline', textDecorationStyle: style }
        };
      }
    );

    // "underline STYLE" variant
    this.commandPatterns.set(
      new RegExp(`^underline\\s+(${decorationStylePattern})$`, 'i'),
      (match) => {
        const style = match[1].toLowerCase();
        return {
          style: { textDecorationLine: 'underline', textDecorationStyle: style }
        };
      }
    );

    // Colored underlines: "red underline", "blue underline"
    const decorationColorPattern = Object.keys(OUTLINE_COLORS).join('|');
    this.commandPatterns.set(
      new RegExp(`^(?:add\\s+)?(?:a\\s+)?(${decorationColorPattern})\\s+underline$`, 'i'),
      (match) => {
        const color = match[1].toLowerCase();
        return {
          style: {
            textDecorationLine: 'underline',
            textDecorationColor: OUTLINE_COLORS[color] || color
          }
        };
      }
    );

    // "underline COLOR" variant
    this.commandPatterns.set(
      new RegExp(`^underline\\s+(${decorationColorPattern})$`, 'i'),
      (match) => {
        const color = match[1].toLowerCase();
        return {
          style: {
            textDecorationLine: 'underline',
            textDecorationColor: OUTLINE_COLORS[color] || color
          }
        };
      }
    );

    // Thick/thin underlines: "thick underline", "thin underline"
    this.commandPatterns.set(
      /^(?:add\s+)?(?:a\s+)?thick\s+underline$/i,
      () => {
        return {
          style: { textDecorationLine: 'underline', textDecorationThickness: '3px' }
        };
      }
    );

    this.commandPatterns.set(
      /^(?:add\s+)?(?:a\s+)?thin\s+underline$/i,
      () => {
        return {
          style: { textDecorationLine: 'underline', textDecorationThickness: '1px' }
        };
      }
    );

    // Overline decoration: "add overline", "overline decoration" (NOT "overline" alone - that's Phase 2 typography preset)
    this.commandPatterns.set(
      /^(?:add\s+(?:an?\s+)?overline|(?:an?\s+)?overline\s+decoration)$/i,
      () => {
        return { style: { textDecorationLine: 'overline' } };
      }
    );

    // Remove overline: "remove overline", "no overline"
    // Smart removal - preserves other decorations like underline or strikethrough
    this.commandPatterns.set(
      /^(?:remove|no|clear|delete)\s+overline$/i,
      (_match, component) => {
        // Get latest component state from store for accurate decoration value
        const latestStyle = getLatestComponentStyle(component);
        const currentDecoration = latestStyle.textDecorationLine || latestStyle.textDecoration || '';
        // Remove 'overline' from the decoration string, keeping others
        const remaining = currentDecoration
          .split(/\s+/)
          .filter(d => d && d !== 'overline' && d !== 'none')
          .join(' ');
        // Clear both style and properties to ensure decoration is removed
        return {
          style: { textDecorationLine: remaining || 'none' },
          properties: { textDecoration: remaining || 'none' }
        };
      }
    );

    // Simple strikethrough/line-through: "strikethrough", "line-through"
    this.commandPatterns.set(
      /^(?:add\s+)?(?:a\s+)?(?:strikethrough|line-?through|strike)$/i,
      () => {
        return { style: { textDecorationLine: 'line-through' } };
      }
    );

    // Remove strikethrough: "remove strikethrough", "no strikethrough"
    // Smart removal - preserves other decorations like underline or overline
    this.commandPatterns.set(
      /^(?:remove|no|clear|delete)\s+(?:strikethrough|line-?through|strike)$/i,
      (_match, component) => {
        // Get latest component state from store for accurate decoration value
        const latestStyle = getLatestComponentStyle(component);
        const currentDecoration = latestStyle.textDecorationLine || latestStyle.textDecoration || '';
        // Remove 'line-through' from the decoration string, keeping others
        const remaining = currentDecoration
          .split(/\s+/)
          .filter(d => d && d !== 'line-through' && d !== 'none')
          .join(' ');
        // Clear both style and properties to ensure decoration is removed
        return {
          style: { textDecorationLine: remaining || 'none' },
          properties: { textDecoration: remaining || 'none' }
        };
      }
    );

    // ========================================================================
    // MULTI-REMOVE DECORATION PATTERNS
    // ========================================================================

    // Remove underline and overline: "remove underline and overline"
    this.commandPatterns.set(
      /^(?:remove|no|clear|delete)\s+underline\s+and\s+overline$/i,
      (_match, component) => {
        const latestStyle = getLatestComponentStyle(component);
        const currentDecoration = latestStyle.textDecorationLine || latestStyle.textDecoration || '';
        const remaining = currentDecoration
          .split(/\s+/)
          .filter(d => d && d !== 'underline' && d !== 'overline' && d !== 'none')
          .join(' ');
        return {
          style: { textDecorationLine: remaining || 'none' },
          properties: { textDecoration: remaining || 'none' }
        };
      }
    );

    // Remove overline and underline (reverse order)
    this.commandPatterns.set(
      /^(?:remove|no|clear|delete)\s+overline\s+and\s+underline$/i,
      (_match, component) => {
        const latestStyle = getLatestComponentStyle(component);
        const currentDecoration = latestStyle.textDecorationLine || latestStyle.textDecoration || '';
        const remaining = currentDecoration
          .split(/\s+/)
          .filter(d => d && d !== 'underline' && d !== 'overline' && d !== 'none')
          .join(' ');
        return {
          style: { textDecorationLine: remaining || 'none' },
          properties: { textDecoration: remaining || 'none' }
        };
      }
    );

    // Remove underline and strikethrough
    this.commandPatterns.set(
      /^(?:remove|no|clear|delete)\s+underline\s+and\s+(?:strikethrough|line-?through|strike)$/i,
      (_match, component) => {
        const latestStyle = getLatestComponentStyle(component);
        const currentDecoration = latestStyle.textDecorationLine || latestStyle.textDecoration || '';
        const remaining = currentDecoration
          .split(/\s+/)
          .filter(d => d && d !== 'underline' && d !== 'line-through' && d !== 'none')
          .join(' ');
        return {
          style: { textDecorationLine: remaining || 'none' },
          properties: { textDecoration: remaining || 'none' }
        };
      }
    );

    // Remove strikethrough and underline (reverse order)
    this.commandPatterns.set(
      /^(?:remove|no|clear|delete)\s+(?:strikethrough|line-?through|strike)\s+and\s+underline$/i,
      (_match, component) => {
        const latestStyle = getLatestComponentStyle(component);
        const currentDecoration = latestStyle.textDecorationLine || latestStyle.textDecoration || '';
        const remaining = currentDecoration
          .split(/\s+/)
          .filter(d => d && d !== 'underline' && d !== 'line-through' && d !== 'none')
          .join(' ');
        return {
          style: { textDecorationLine: remaining || 'none' },
          properties: { textDecoration: remaining || 'none' }
        };
      }
    );

    // Remove overline and strikethrough
    this.commandPatterns.set(
      /^(?:remove|no|clear|delete)\s+overline\s+and\s+(?:strikethrough|line-?through|strike)$/i,
      (_match, component) => {
        const latestStyle = getLatestComponentStyle(component);
        const currentDecoration = latestStyle.textDecorationLine || latestStyle.textDecoration || '';
        const remaining = currentDecoration
          .split(/\s+/)
          .filter(d => d && d !== 'overline' && d !== 'line-through' && d !== 'none')
          .join(' ');
        return {
          style: { textDecorationLine: remaining || 'none' },
          properties: { textDecoration: remaining || 'none' }
        };
      }
    );

    // Remove strikethrough and overline (reverse order)
    this.commandPatterns.set(
      /^(?:remove|no|clear|delete)\s+(?:strikethrough|line-?through|strike)\s+and\s+overline$/i,
      (_match, component) => {
        const latestStyle = getLatestComponentStyle(component);
        const currentDecoration = latestStyle.textDecorationLine || latestStyle.textDecoration || '';
        const remaining = currentDecoration
          .split(/\s+/)
          .filter(d => d && d !== 'overline' && d !== 'line-through' && d !== 'none')
          .join(' ');
        return {
          style: { textDecorationLine: remaining || 'none' },
          properties: { textDecoration: remaining || 'none' }
        };
      }
    );

    // Remove all decorations: "remove all decorations", "clear all decorations", "no decorations"
    this.commandPatterns.set(
      /^(?:remove|no|clear|delete)\s+all\s+(?:text\s+)?decorations?$/i,
      () => {
        return {
          style: { textDecorationLine: 'none', textDecorationStyle: 'solid', textDecorationColor: 'currentColor' },
          properties: { textDecoration: 'none' }
        };
      }
    );

    // Styled strikethroughs: "wavy strikethrough", "dashed strikethrough", "dotted strikethrough"
    this.commandPatterns.set(
      new RegExp(`^(?:add\\s+)?(?:a\\s+)?(${decorationStylePattern})\\s+(?:strikethrough|line-?through|strike)$`, 'i'),
      (match) => {
        const style = match[1].toLowerCase();
        return {
          style: { textDecorationLine: 'line-through', textDecorationStyle: style }
        };
      }
    );

    // "strikethrough STYLE" variant
    this.commandPatterns.set(
      new RegExp(`^(?:strikethrough|line-?through|strike)\\s+(${decorationStylePattern})$`, 'i'),
      (match) => {
        const style = match[1].toLowerCase();
        return {
          style: { textDecorationLine: 'line-through', textDecorationStyle: style }
        };
      }
    );

    // Colored strikethrough: "red strikethrough", "blue line-through"
    this.commandPatterns.set(
      new RegExp(`^(?:add\\s+)?(?:a\\s+)?(${decorationColorPattern})\\s+(?:strikethrough|line-?through|strike)$`, 'i'),
      (match) => {
        const color = match[1].toLowerCase();
        return {
          style: {
            textDecorationLine: 'line-through',
            textDecorationColor: OUTLINE_COLORS[color] || color
          }
        };
      }
    );

    // Underline and overline: "underline and overline", "add underline and overline", "both underline and overline"
    this.commandPatterns.set(
      /^(?:add\s+)?(?:both\s+)?underline\s+and\s+overline$/i,
      () => {
        return { style: { textDecorationLine: 'underline overline' } };
      }
    );

    // Overline and underline (reverse order)
    this.commandPatterns.set(
      /^(?:add\s+)?(?:both\s+)?overline\s+and\s+underline$/i,
      () => {
        return { style: { textDecorationLine: 'overline underline' } };
      }
    );

    // Combined decorations: "underline and strikethrough", "strikethrough and underline"
    this.commandPatterns.set(
      /^(?:add\s+)?underline\s+and\s+(?:line-?through|strikethrough|strike)$/i,
      () => {
        return { style: { textDecorationLine: 'underline line-through' } };
      }
    );

    this.commandPatterns.set(
      /^(?:add\s+)?(?:line-?through|strikethrough|strike)\s+and\s+underline$/i,
      () => {
        return { style: { textDecorationLine: 'line-through underline' } };
      }
    );

    // Strikethrough and overline: "strikethrough and overline"
    this.commandPatterns.set(
      /^(?:add\s+)?(?:line-?through|strikethrough|strike)\s+and\s+overline$/i,
      () => {
        return { style: { textDecorationLine: 'line-through overline' } };
      }
    );

    // Overline and strikethrough (reverse order)
    this.commandPatterns.set(
      /^(?:add\s+)?overline\s+and\s+(?:line-?through|strikethrough|strike)$/i,
      () => {
        return { style: { textDecorationLine: 'overline line-through' } };
      }
    );

    // All three decorations: "underline overline and strikethrough"
    this.commandPatterns.set(
      /^(?:add\s+)?(?:underline|overline|line-?through|strikethrough|strike)(?:\s+(?:and\s+)?(?:underline|overline|line-?through|strikethrough|strike)){2}$/i,
      (match) => {
        const cmd = match[0].toLowerCase();
        const decorations: string[] = [];
        if (cmd.includes('underline')) decorations.push('underline');
        if (cmd.includes('overline')) decorations.push('overline');
        if (cmd.includes('line-through') || cmd.includes('strikethrough') || cmd.includes('strike')) decorations.push('line-through');
        return { style: { textDecorationLine: decorations.join(' ') } };
      }
    );

    // ========================================================================
    // 3.3 TEXT OUTLINE & STROKE
    // ========================================================================

    // Basic outline: "add outline", "text outline", "outline text"
    this.commandPatterns.set(
      /^(?:add\s+)?(?:a\s+)?(?:text\s+)?outline$/i,
      () => {
        return {
          style: {
            WebkitTextStroke: '1px black',
            textStroke: '1px black'
          }
        };
      }
    );

    // "outline text" variant
    this.commandPatterns.set(
      /^outline\s+(?:the\s+)?text$/i,
      () => {
        return {
          style: {
            WebkitTextStroke: '1px black',
            textStroke: '1px black'
          }
        };
      }
    );

    // Colored outline: "blue outline", "outline red", "white outline"
    const outlineColorPattern = Object.keys(OUTLINE_COLORS).join('|');
    this.commandPatterns.set(
      new RegExp(`^(?:add\\s+)?(?:a\\s+)?(${outlineColorPattern})\\s+(?:text\\s+)?outline$`, 'i'),
      (match) => {
        const color = match[1].toLowerCase();
        const hexColor = OUTLINE_COLORS[color] || color;
        return {
          style: {
            WebkitTextStroke: `1px ${hexColor}`,
            textStroke: `1px ${hexColor}`
          }
        };
      }
    );

    // "outline COLOR" variant
    this.commandPatterns.set(
      new RegExp(`^(?:text\\s+)?outline\\s+(${outlineColorPattern})$`, 'i'),
      (match) => {
        const color = match[1].toLowerCase();
        const hexColor = OUTLINE_COLORS[color] || color;
        return {
          style: {
            WebkitTextStroke: `1px ${hexColor}`,
            textStroke: `1px ${hexColor}`
          }
        };
      }
    );

    // Thick/thin outline: "thick outline", "thin outline"
    // Preserves existing color when changing thickness
    this.commandPatterns.set(
      /^(?:add\s+)?(?:a\s+)?thick\s+(?:text\s+)?outline$/i,
      (_match, component) => {
        // Get current outline color from latest component state in store
        let currentStroke = '';
        try {
          const store = useEditorStore.getState();
          const latestComponent = store.components.find(c => c.id === component?.id);
          currentStroke = asString(latestComponent?.style?.WebkitTextStroke) ??
                          asString(component?.style?.WebkitTextStroke) ??
                          '';
        } catch {
          currentStroke = asString(component?.style?.WebkitTextStroke) ?? '';
        }
        const colorMatch = currentStroke.match(/\d+(?:\.\d+)?px\s+(.+)/);
        const currentColor = colorMatch ? colorMatch[1] : 'black';
        return {
          style: {
            WebkitTextStroke: `2px ${currentColor}`,
            textStroke: `2px ${currentColor}`
          }
        };
      }
    );

    this.commandPatterns.set(
      /^(?:add\s+)?(?:a\s+)?thin\s+(?:text\s+)?outline$/i,
      (_match, component) => {
        // Get current outline color from latest component state in store
        let currentStroke = '';
        try {
          const store = useEditorStore.getState();
          const latestComponent = store.components.find(c => c.id === component?.id);
          currentStroke = asString(latestComponent?.style?.WebkitTextStroke) ??
                          asString(component?.style?.WebkitTextStroke) ??
                          '';
        } catch {
          currentStroke = asString(component?.style?.WebkitTextStroke) ?? '';
        }
        const colorMatch = currentStroke.match(/\d+(?:\.\d+)?px\s+(.+)/);
        const currentColor = colorMatch ? colorMatch[1] : 'black';
        return {
          style: {
            WebkitTextStroke: `0.5px ${currentColor}`,
            textStroke: `0.5px ${currentColor}`
          }
        };
      }
    );

    // Remove outline: "remove outline", "no outline"
    this.commandPatterns.set(
      /^(?:remove|no|clear)\s+(?:text\s+)?outline$/i,
      () => {
        return {
          style: {
            WebkitTextStroke: '0',
            textStroke: '0'
          }
        };
      }
    );

    // ========================================================================
    // 3.4 TEXT GRADIENT
    // ========================================================================

    // Linear gradient: "gradient text blue to green", "gradient from red to orange"
    this.commandPatterns.set(
      /^(?:make\s+)?gradient\s+(?:text\s+)?(?:from\s+)?(\w+)\s+to\s+(\w+)$/i,
      (match) => {
        const color1 = this.parseEnhancedColorValue(match[1]) || match[1].toLowerCase();
        const color2 = this.parseEnhancedColorValue(match[2]) || match[2].toLowerCase();
        return {
          style: {
            background: `linear-gradient(90deg, ${color1}, ${color2})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            backgroundColor: 'transparent'
          }
        };
      }
    );

    // "text gradient COLOR to COLOR" variant
    this.commandPatterns.set(
      /^(?:text\s+)?gradient\s+(\w+)\s+to\s+(\w+)$/i,
      (match) => {
        const color1 = this.parseEnhancedColorValue(match[1]) || match[1].toLowerCase();
        const color2 = this.parseEnhancedColorValue(match[2]) || match[2].toLowerCase();
        return {
          style: {
            background: `linear-gradient(90deg, ${color1}, ${color2})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            backgroundColor: 'transparent'
          }
        };
      }
    );

    // Rainbow text: "rainbow text", "make it rainbow"
    this.commandPatterns.set(
      /^(?:make\s+(?:it\s+)?)?rainbow\s*(?:text)?$/i,
      () => {
        return {
          style: {
            background: 'linear-gradient(90deg, #EF4444, #F97316, #EAB308, #22C55E, #3B82F6, #A855F7)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            backgroundColor: 'transparent'
          }
        };
      }
    );

    // Brand gradient: "brand gradient text", "terramatrix gradient"
    this.commandPatterns.set(
      /^(?:brand|terramatrix)\s+gradient\s*(?:text)?$/i,
      () => {
        return {
          style: {
            background: `linear-gradient(90deg, ${BRAND_TEXT_COLORS['forest green']}, ${BRAND_TEXT_COLORS['seafoam']}, ${BRAND_TEXT_COLORS['blue grotto']})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            backgroundColor: 'transparent'
          }
        };
      }
    );

    // Gradient text shortcut: "gradient text" (uses brand colors)
    this.commandPatterns.set(
      /^gradient\s+text$/i,
      () => {
        return {
          style: {
            background: `linear-gradient(90deg, ${BRAND_TEXT_COLORS['forest green']}, ${BRAND_TEXT_COLORS['blue grotto']})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            backgroundColor: 'transparent'
          }
        };
      }
    );

    // Sunset gradient: "sunset gradient", "sunset text"
    this.commandPatterns.set(
      /^sunset\s+(?:gradient|text)?$/i,
      () => {
        return {
          style: {
            background: 'linear-gradient(90deg, #f97316, #ef4444, #ec4899)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            backgroundColor: 'transparent'
          }
        };
      }
    );

    // Ocean gradient: "ocean gradient", "ocean text"
    this.commandPatterns.set(
      /^ocean\s+(?:gradient|text)?$/i,
      () => {
        return {
          style: {
            background: 'linear-gradient(90deg, #06b6d4, #3b82f6, #8b5cf6)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            backgroundColor: 'transparent'
          }
        };
      }
    );

    // Fire gradient: "fire gradient", "fire text"
    this.commandPatterns.set(
      /^fire\s+(?:gradient|text)?$/i,
      () => {
        return {
          style: {
            background: 'linear-gradient(90deg, #fbbf24, #f97316, #dc2626)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            backgroundColor: 'transparent'
          }
        };
      }
    );

    // Forest gradient: "forest gradient", "nature gradient"
    this.commandPatterns.set(
      /^(?:forest|nature)\s+(?:gradient|text)?$/i,
      () => {
        return {
          style: {
            background: 'linear-gradient(90deg, #22c55e, #16a34a, #15803d)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            backgroundColor: 'transparent'
          }
        };
      }
    );

    // Remove gradient: "remove gradient", "no gradient", "clear gradient"
    this.commandPatterns.set(
      /^(?:remove|no|clear)\s+(?:text\s+)?gradient$/i,
      () => {
        return {
          style: {
            background: 'none',
            WebkitBackgroundClip: 'unset',
            WebkitTextFillColor: 'unset',
            backgroundClip: 'unset'
          }
        };
      }
    );

    logger.debug("TextCommandHandler effects patterns initialized", {
      shadowPresets: Object.keys(TEXT_SHADOW_PRESETS).length,
      glowPresets: Object.keys(GLOW_PRESETS).length,
      decorationStyles: DECORATION_STYLES.length,
      outlineColors: Object.keys(OUTLINE_COLORS).length,
    });
  }


  // ============================================================================
  // Phase 4: Responsive Text Styling - Initialization
  // ============================================================================

  /**
   * Phase 4: Initialize responsive text patterns
   * 4.1 Breakpoint-Specific Commands: "on mobile h3", "tablet: center", "desktop bold"
   * 4.2 Fluid Typography: clamp() based responsive font sizing
   */
  private initializeResponsivePatterns(): void {
    // ========================================================================
    // 4.1 BREAKPOINT-SPECIFIC COMMANDS
    // ========================================================================

    // Pattern: "on mobile X", "on tablet X", "on desktop X"
    this.commandPatterns.set(
      /^on\s+(mobile|tablet|desktop)\s+(.+)$/i,
      (match, component) => {
        const breakpoint = parseBreakpoint(match[1]);
        if (!breakpoint) return null;
        const subCommand = match[2];
        return this.handleBreakpointCommand(breakpoint, subCommand, component);
      }
    );

    // Pattern: "mobile: X", "tablet: X", "desktop: X"
    this.commandPatterns.set(
      /^(mobile|tablet|desktop):\s*(.+)$/i,
      (match, component) => {
        const breakpoint = parseBreakpoint(match[1]);
        if (!breakpoint) return null;
        const subCommand = match[2];
        return this.handleBreakpointCommand(breakpoint, subCommand, component);
      }
    );

    // Pattern: "only on mobile X", "only on tablet X", "only on desktop X"
    this.commandPatterns.set(
      /^only\s+on\s+(mobile|tablet|desktop)\s+(.+)$/i,
      (match, component) => {
        const breakpoint = parseBreakpoint(match[1]);
        if (!breakpoint) return null;
        const subCommand = match[2];
        return this.handleBreakpointCommand(breakpoint, subCommand, component);
      }
    );

    // Pattern: "mobile bold", "tablet center", "desktop h1" (breakpoint first, then property)
    this.commandPatterns.set(
      /^(mobile|tablet|desktop)\s+(bold|italic|underline|center|left|right|justify|h1|h2|h3|h4|h5|h6|uppercase|lowercase|capitalize|normal)$/i,
      (match, component) => {
        const breakpoint = parseBreakpoint(match[1]);
        if (!breakpoint) return null;
        const subCommand = match[2];
        return this.handleBreakpointCommand(breakpoint, subCommand, component);
      }
    );

    // Pattern: "mobile 14px", "tablet 18px", "desktop 24px" (breakpoint + font size)
    // This pattern handles font sizes directly since "14px" alone doesn't have a matching pattern
    this.commandPatterns.set(
      /^(mobile|tablet|desktop)\s+(\d+(?:\.\d+)?(?:px|rem|em|pt|%|vw))$/i,
      (match) => {
        const breakpoint = parseBreakpoint(match[1]);
        if (!breakpoint) return null;
        const fontSize = match[2];
        // Directly return responsive styles for font size (no need to parse through sub-command)
        return {
          responsiveStyles: { [breakpoint]: { fontSize: fontSize } }
        };
      }
    );

    // Pattern: "remove bold from mobile", "remove italic from tablet", "clear underline on desktop"
    this.commandPatterns.set(
      /^(?:remove|clear|no)\s+(bold|italic|underline|center|uppercase|lowercase|capitalize)\s+(?:from|on|for)\s+(mobile|tablet|desktop)$/i,
      (match) => {
        const property = match[1].toLowerCase();
        const breakpoint = parseBreakpoint(match[2]);
        if (!breakpoint) return null;
        // Map property to its reset value
        const resetValues: Record<string, Record<string, string>> = {
          'bold': { fontWeight: 'normal' },
          'italic': { fontStyle: 'normal' },
          'underline': { textDecoration: 'none' },
          'center': { textAlign: 'left' },
          'uppercase': { textTransform: 'none' },
          'lowercase': { textTransform: 'none' },
          'capitalize': { textTransform: 'none' },
        };

        const styleReset = resetValues[property] || {};
        return {
          responsiveStyles: { [breakpoint]: styleReset }
        };
      }
    );

    // Pattern: "unbold on mobile", "unitalic on tablet" (alternative syntax)
    this.commandPatterns.set(
      /^(?:un)(bold|italic)\s+(?:on|for)\s+(mobile|tablet|desktop)$/i,
      (match) => {
        const property = match[1].toLowerCase();
        const breakpoint = parseBreakpoint(match[2]);
        if (!breakpoint) return null;
        const resetValues: Record<string, Record<string, string>> = {
          'bold': { fontWeight: 'normal' },
          'italic': { fontStyle: 'normal' },
        };

        const styleReset = resetValues[property] || {};
        return {
          responsiveStyles: { [breakpoint]: styleReset }
        };
      }
    );

    // ========================================================================
    // 4.2 FLUID TYPOGRAPHY PATTERNS
    // ========================================================================

    // Fluid preset patterns: "fluid xs", "fluid sm", "fluid lg", etc.
    const fluidPresetNames = Object.keys(FLUID_SIZE_PRESETS).join('|');
    this.commandPatterns.set(
      new RegExp(`^fluid\\s+(${fluidPresetNames})$`, 'i'),
      (match) => {
        const preset = match[1].toLowerCase();
        const clampValue = FLUID_SIZE_PRESETS[preset];
        return { style: { fontSize: clampValue } };
      }
    );

    // "responsive text X" variant: "responsive text xs", "responsive text lg"
    this.commandPatterns.set(
      new RegExp(`^responsive\\s+(?:text\\s+)?(${fluidPresetNames})$`, 'i'),
      (match) => {
        const preset = match[1].toLowerCase();
        const clampValue = FLUID_SIZE_PRESETS[preset];
        return { style: { fontSize: clampValue } };
      }
    );

    // "clamp text X" variant: "clamp text sm", "clamp text 2xl"
    this.commandPatterns.set(
      new RegExp(`^clamp\\s+(?:text\\s+)?(${fluidPresetNames})$`, 'i'),
      (match) => {
        const preset = match[1].toLowerCase();
        const clampValue = FLUID_SIZE_PRESETS[preset];
        return { style: { fontSize: clampValue } };
      }
    );

    // Fluid range patterns: "fluid 16 to 24", "fluid 14px to 20px"
    this.commandPatterns.set(
      /^fluid\s+(\d+(?:\.\d+)?)\s*(?:px)?\s+to\s+(\d+(?:\.\d+)?)\s*(?:px)?$/i,
      (match) => {
        const minSize = parseFloat(match[1]);
        const maxSize = parseFloat(match[2]);
        // Generate clamp() with viewport-based preferred value
        // Using vw for viewport responsiveness: preferred = min + (max-min) * 0.5vw factor
        const preferredVw = ((minSize + maxSize) / 2 / 16 * 2.5).toFixed(2);
        const clampValue = `clamp(${minSize}px, ${preferredVw}vw, ${maxSize}px)`;
        return { style: { fontSize: clampValue } };
      }
    );

    // "responsive X to Y", "responsive 14-20"
    this.commandPatterns.set(
      /^responsive\s+(\d+(?:\.\d+)?)\s*(?:px)?\s*[-–—to]\s*(\d+(?:\.\d+)?)\s*(?:px)?$/i,
      (match) => {
        const minSize = parseFloat(match[1]);
        const maxSize = parseFloat(match[2]);
        const preferredVw = ((minSize + maxSize) / 2 / 16 * 2.5).toFixed(2);
        const clampValue = `clamp(${minSize}px, ${preferredVw}vw, ${maxSize}px)`;
        return { style: { fontSize: clampValue } };
      }
    );

    // "fluid text" or "responsive font" (uses base preset)
    this.commandPatterns.set(
      /^(?:fluid|responsive)\s+(?:text|font|typography)$/i,
      () => {
        const clampValue = FLUID_SIZE_PRESETS['base'];
        return { style: { fontSize: clampValue } };
      }
    );

    // "make text fluid", "make font responsive"
    this.commandPatterns.set(
      /^make\s+(?:text|font|it)\s+(?:fluid|responsive)$/i,
      () => {
        const clampValue = FLUID_SIZE_PRESETS['base'];
        return { style: { fontSize: clampValue } };
      }
    );

    // Fluid heading presets: "fluid h1", "fluid h2", "responsive h3"
    const fluidHeadingMap: Record<string, string> = {
      'h1': FLUID_SIZE_PRESETS['5xl'],
      'h2': FLUID_SIZE_PRESETS['4xl'],
      'h3': FLUID_SIZE_PRESETS['3xl'],
      'h4': FLUID_SIZE_PRESETS['2xl'],
      'h5': FLUID_SIZE_PRESETS['xl'],
      'h6': FLUID_SIZE_PRESETS['lg'],
    };
    this.commandPatterns.set(
      /^(?:fluid|responsive)\s+(h[1-6])$/i,
      (match) => {
        const heading = match[1].toLowerCase();
        const clampValue = fluidHeadingMap[heading] || FLUID_SIZE_PRESETS['base'];
        return { style: { fontSize: clampValue } };
      }
    );

    // Remove fluid/responsive sizing: "fixed text", "no fluid", "remove responsive"
    this.commandPatterns.set(
      /^(?:fixed\s+text|no\s+fluid|remove\s+(?:fluid|responsive)|static\s+text)$/i,
      (match, component) => {
        // Get current font size and convert to fixed px value if it's a clamp
        const currentFontSize = asString(component?.style?.fontSize);
        let fixedSize = '16px'; // default
        if (currentFontSize) {
          // If it's a clamp value, extract the max value
          const clampMatch = currentFontSize.match(/clamp\([^,]+,\s*[^,]+,\s*([^)]+)\)/);
          if (clampMatch) {
            fixedSize = clampMatch[1].trim();
          } else if (!currentFontSize.includes('clamp')) {
            fixedSize = currentFontSize;
          }
        }
        return { style: { fontSize: fixedSize } };
      }
    );

    logger.debug("TextCommandHandler responsive patterns initialized", {
      breakpointPatterns: "5 (on X, X:, only on X, X property, X size)",
      fluidPresets: Object.keys(FLUID_SIZE_PRESETS).length,
      fluidHeadingPresets: "6 (h1-h6)",
    });
  }

  // ============================================================================
  // Phase 4: Helper Methods
  // ============================================================================

  /**
   * Handle breakpoint-specific commands by parsing sub-command and wrapping in responsiveStyles
   * Similar to FlexContainerCommandHandler.handleBreakpointCommand
   */
  private handleBreakpointCommand(
    breakpoint: 'mobile' | 'tablet' | 'desktop',
    subCommand: string,
    component: Component
  ): PropertyChange | null {
    const trimmedSubCommand = subCommand.trim();

    console.log(`[TextCommandHandler Phase 4] Breakpoint command detected`);
    console.log(`[TextCommandHandler Phase 4] Breakpoint: ${breakpoint}`);
    console.log(`[TextCommandHandler Phase 4] Sub-command: "${trimmedSubCommand}"`);

    // Parse sub-command using existing patterns (excluding breakpoint patterns)
    const subResult = this.parseSubCommand(trimmedSubCommand, component);

    if (subResult) {
      console.log(`[TextCommandHandler Phase 4] Sub-command parsed successfully`);
      console.log(`[TextCommandHandler Phase 4] Sub-result:`, JSON.stringify(subResult));

      // Build responsive styles from the sub-result
      const responsiveStyle: Record<string, unknown> = {};

      // Merge style changes directly
      if (subResult.style) {
        Object.assign(responsiveStyle, subResult.style);
      }

      // Convert properties to styles for breakpoint-specific application
      if (subResult.properties) {
        // Font weight
        if (subResult.properties.fontWeight) {
          responsiveStyle.fontWeight = subResult.properties.fontWeight;
        }
        // Font size
        if (subResult.properties.fontSize !== undefined) {
          const sizeValue = subResult.properties.fontSize;
          responsiveStyle.fontSize = typeof sizeValue === 'number' ? `${sizeValue}px` : sizeValue;
        }
        // Font style (italic)
        if (subResult.properties.fontStyle) {
          responsiveStyle.fontStyle = subResult.properties.fontStyle;
        }
        // Font family
        if (subResult.properties.fontFamily) {
          responsiveStyle.fontFamily = subResult.properties.fontFamily;
        }
        // Text alignment
        if (subResult.properties.textAlign) {
          responsiveStyle.textAlign = subResult.properties.textAlign;
        }
        // Text decoration
        if (subResult.properties.textDecoration) {
          responsiveStyle.textDecoration = subResult.properties.textDecoration;
        }
        // Text transform
        if (subResult.properties.textTransform) {
          responsiveStyle.textTransform = subResult.properties.textTransform;
        }
        // Color
        if (subResult.properties.color) {
          responsiveStyle.color = subResult.properties.color;
        }
        // Background color
        if (subResult.properties.backgroundColor) {
          responsiveStyle.backgroundColor = subResult.properties.backgroundColor;
        }
        // Line height
        if (subResult.properties.lineHeight !== undefined) {
          responsiveStyle.lineHeight = subResult.properties.lineHeight;
        }
        // Letter spacing
        if (subResult.properties.letterSpacing !== undefined) {
          responsiveStyle.letterSpacing = subResult.properties.letterSpacing;
        }
      }

      console.log(`[TextCommandHandler Phase 4] Responsive styles for ${breakpoint}:`, JSON.stringify(responsiveStyle));

      // Return with responsiveStyles wrapper
      return {
        responsiveStyles: { [breakpoint]: responsiveStyle }
      };
    }

    console.log(`[TextCommandHandler Phase 4] Sub-command not recognized: "${trimmedSubCommand}"`);
    return null;
  }

  /**
   * Parse a sub-command using existing patterns, excluding breakpoint patterns
   * Similar to FlexContainerCommandHandler.parseSubCommand
   */
  private parseSubCommand(command: string, component: Component): PropertyChange | null {
    const normalizedCommand = command.trim().toLowerCase();

    console.log(`[TextCommandHandler Phase 4] parseSubCommand: "${normalizedCommand}"`);

    // Check if this looks like a nested breakpoint command (prevent recursion)
    const breakpointPrefixPatterns = [
      /^on\s+(mobile|tablet|desktop)\s+/i,
      /^(mobile|tablet|desktop):\s*/i,
      /^only\s+on\s+(mobile|tablet|desktop)\s+/i,
      /^(mobile|tablet|desktop)\s+(bold|italic|underline|center|left|right|justify|h[1-6])/i
    ];

    for (const pattern of breakpointPrefixPatterns) {
      if (pattern.test(normalizedCommand)) {
        console.log(`[TextCommandHandler Phase 4] Nested breakpoint pattern detected, skipping`);
        return null;
      }
    }

    // Match against existing patterns, skipping breakpoint patterns
    for (const [pattern, handler] of this.commandPatterns) {
      // Skip patterns that contain breakpoint references (our Phase 4 patterns)
      const patternSource = pattern.source;
      if (
        patternSource.includes('mobile|tablet|desktop') ||
        (patternSource.includes('on\\s+') && patternSource.includes('mobile'))
      ) {
        continue;
      }

      const match = normalizedCommand.match(pattern);
      if (match) {
        const result = handler(match, component);
        if (result) {
          console.log(`[TextCommandHandler Phase 4] Sub-command matched pattern`);
          return result;
        }
      }
    }

    console.log(`[TextCommandHandler Phase 4] No pattern matched sub-command`);
    return null;
  }

  /**
   * Phase 1: Enhanced color value parser
   * Supports extended CSS colors, brand colors, semantic colors, hex, RGB, HSL
   */
  private parseEnhancedColorValue(value: string): string | null {
    const trimmed = value.trim().toLowerCase();

    // 1. Check hex colors (#RGB, #RRGGBB, #RRGGBBAA)
    if (trimmed.startsWith('#')) {
      const hexMatch = trimmed.match(/^#([0-9a-fA-F]{3,8})$/);
      if (hexMatch) {
        let hex = hexMatch[1];
        if (hex.length === 3) {
          hex = hex.split('').map(c => c + c).join('');
        }
        return `#${hex}`;
      }
    }

    // 2. Check RGB/RGBA
    const rgbMatch = trimmed.match(/rgba?\s*\([^)]+\)/i);
    if (rgbMatch) {
      return rgbMatch[0];
    }

    // 3. Check HSL/HSLA
    const hslMatch = trimmed.match(/hsla?\s*\([^)]+\)/i);
    if (hslMatch) {
      return hslMatch[0];
    }

    // 4. Check brand colors (handles multi-word like "forest green")
    const normalizedKey = trimmed.replace(/\s+/g, ' ');
    if (BRAND_TEXT_COLORS[normalizedKey]) {
      return BRAND_TEXT_COLORS[normalizedKey];
    }

    // 5. Check semantic colors
    if (SEMANTIC_COLORS[trimmed]) {
      return SEMANTIC_COLORS[trimmed];
    }

    // 6. Check extended CSS colors
    if (EXTENDED_CSS_COLORS.includes(trimmed)) {
      return trimmed;
    }

    // 7. Fallback to base parseColorValue
    return this.parseColorValue(value);
  }

  canHandle(command: string, component: Component): boolean {
    // Check if it's a text component
    if (component.type !== ComponentType.TEXT) {
      return false;
    }

    // For text components, we can handle almost anything
    // First check for text-specific keywords
    const textKeywords = [
      'text', 'font', 'size', 'color', 'align', 'bold', 'italic',
      'underline', 'content', 'write', 'type', 'heading', 'paragraph',
      'uppercase', 'lowercase', 'capitalize', 'spacing', 'shadow',
      'opacity', 'weight', 'style', 'family', 'decoration',
      // Phase 2.2: Relative font keywords
      'bigger', 'smaller', 'larger', 'tinier', 'tighter', 'looser',
      'double', 'half', 'increase', 'decrease', 'reduce', 'step',
      // Phase 1: Enhanced color keywords
      'darker', 'lighter', 'primary', 'secondary', 'brand', 'accent',
      'success', 'error', 'danger', 'warning', 'info', 'forest', 'sage',
      'seafoam', 'grotto', 'desert', 'chartreuse', 'charcoal', 'muted',
      'hsl', 'rgb', 'rgba', 'hsla',
      // Phase 2: Advanced Typography keywords
      'thin', 'hairline', 'extralight', 'ultralight', 'light', 'medium',
      'semibold', 'demibold', 'extrabold', 'ultrabold', 'heavy', 'black',
      'bolder', 'serif', 'sans', 'monospace', 'mono', 'system',
      'arial', 'helvetica', 'georgia', 'times', 'roboto', 'inter', 'poppins',
      'display', 'caption', 'overline', 'eyebrow', 'label', 'lead', 'blockquote',
      'leading', 'tracking', 'snug', 'relaxed', 'loose', 'tight',
      // Phase 3: Text Effects & Visual Enhancements
      'glow', 'neon', 'soft', 'hard', 'crisp', 'outline', 'stroke',
      'gradient', 'rainbow', 'wavy', 'dashed', 'dotted', 'strikethrough',
      'thick', 'strikethrough', 'line-through'
    ];

    const lowerCommand = command.toLowerCase().trim();

    // If it contains any text keywords, we can handle it
    if (textKeywords.some(keyword => lowerCommand.includes(keyword))) {
      return true;
    }

    // Phase 2.2: Check for percentage-based font patterns
    if (/^\d+%\s*(bigger|smaller|larger|tinier)\s*(text|font)$/i.test(lowerCommand)) {
      return true;
    }

    // Phase 3: Check for shadow size patterns (xs, sm, md, lg, xl) - both "shadow xs" and "xs shadow" formats
    if (/\b(shadow|glow)\s*(xs|sm|md|lg|xl)\b/i.test(lowerCommand) ||
        /\b(xs|sm|md|lg|xl)\s*(shadow|glow)\b/i.test(lowerCommand)) {
      return true;
    }

    // If it's a short command (likely direct text), we can handle it
    if (lowerCommand.length > 0 && lowerCommand.split(' ').length <= 5) {
      // Unless it's clearly a non-text command
      const nonTextPatterns = [
        /^(add|create|insert|delete|remove)\s+(component|button|image|chart|table)/i,
        /^navigate/i,
        /^save/i,
        /^export/i,
        /^import/i
      ];

      return !nonTextPatterns.some(pattern => pattern.test(lowerCommand));
    }

    return false;
  }

  async parseCommand(
    command: string,
    component: Component,
    textSelection?: TextSelectionContext
  ): Promise<CommandResult> {
    try {
      // Phase 5: Check for text selection context and process selection-specific commands first
      if (textSelection && textSelection.selectedText && !textSelection.isCursor) {
        const selectionResult = this.parseSelectionCommand(command, component, textSelection);
        if (selectionResult) {
          return selectionResult;
        }
        // If no selection-specific pattern matched, fall through to normal parsing
      }

      const headingLevelChange = parseHeadingLevelPresetChange(command);
      if (headingLevelChange) {
        return {
          success: true,
          command: {
            type: 'style',
            changes: headingLevelChange,
            confidence: 0.95,
            description: 'Updated heading level'
          }
        };
      }

      // Check for template commands first
      const template = textTemplates.findTemplate(command);
      if (template) {
        return {
          success: true,
          command: {
            type: 'template',
            changes: {
              properties: template.properties,
              style: template.style
            },
            confidence: 0.9,
            description: `Applied template: ${template.name}`
          }
        };
      }

      // Try pattern matching first
      const changes = this.matchPatterns(command, component);
      if (changes) {
        const validation = this.validateChanges(changes, component);
        if (!validation.valid) {
          return {
            success: false,
            error: validation.error,
            suggestions: await this.getSuggestions(component)
          };
        }

        return {
          success: true,
          command: {
            type: this.determineCommandType(changes),
            changes,
            confidence: this.calculateConfidence(command, changes),
            description: this.generateDescription(changes, component)
          },
          suggestions: await this.getSuggestions(component)
        };
      }

      // Try complex command parsing (handles "and", "with")
      const complexChanges = this.parseComplexCommand(command, component);
      if (complexChanges) {
        const validation = this.validateChanges(complexChanges, component);
        if (!validation.valid) {
          return {
            success: false,
            error: validation.error,
            suggestions: await this.getSuggestions(component)
          };
        }

        return {
          success: true,
          command: {
            type: this.determineCommandType(complexChanges),
            changes: complexChanges,
            confidence: this.calculateConfidence(command, complexChanges),
            description: this.generateDescription(complexChanges, component)
          },
          suggestions: await this.getSuggestions(component)
        };
      }

      // Phase 5: Content changes now ONLY happen via explicit quoted patterns:
      // - "text", 'text', `text`
      // - content: "text", content = 'text'
      // - write "text", type 'text', display `text`
      // - set text to "text", set content to 'text'
      // This prevents confusion between natural language commands and content replacement.

      return {
        success: false,
        error: "Command not recognized",
        suggestions: await this.getSuggestions(component)
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to parse command",
        suggestions: await this.getSuggestions(component)
      };
    }
  }

  /**
   * Parse complex multi-property commands
   */
  private parseComplexCommand(command: string, component: Component): PropertyChange | null {
    const propertyChanges: NonNullable<PropertyChange['properties']> = {};
    const styleChanges: NonNullable<PropertyChange['style']> = {};
    const changes: PropertyChange = {
      properties: propertyChanges,
      style: styleChanges
    };

    // Handle compound commands like "make text bold and blue with size 20"
    // Split on 'and' or 'with' but preserve the original case for matching
    const parts = command.split(/\s+(?:and|with|also)\s+/i);

    let hasAnyChanges = false;

    parts.forEach(part => {
      const trimmedPart = part.trim();

      // Try to match each part individually
      let partChanges = this.matchPatterns(trimmedPart, component);

      // If no match, try to infer the intent
      if (!partChanges && trimmedPart) {
        // Handle "size 20" -> "font size 20" or "size to 20"
        if (/^size \d+$/i.test(trimmedPart)) {
          const sizeMatch = trimmedPart.match(/size (\d+)/i);
          if (sizeMatch) {
            partChanges = { properties: { fontSize: parseInt(sizeMatch[1]) } };
          }
        }
      }

      if (partChanges) {
        hasAnyChanges = true;
        if (partChanges.properties) {
          Object.assign(propertyChanges, partChanges.properties);
        }
        if (partChanges.style) {
          Object.assign(styleChanges, partChanges.style);
        }
        if (partChanges.size) {
          changes.size = { ...changes.size, ...partChanges.size };
        }
      }
    });

    // Return null if no changes were parsed
    if (!hasAnyChanges) {
      return null;
    }

    // Clean up empty objects
    if (Object.keys(propertyChanges).length === 0) {
      delete changes.properties;
    }
    if (Object.keys(styleChanges).length === 0) {
      delete changes.style;
    }

    return changes;
  }

  async getExampleCommands(componentType: ComponentType): Promise<string[]> {
    if (componentType !== ComponentType.TEXT) {
      return [];
    }

    return [
      // Content
      'Set text to "Hello World"',
      'Write "Welcome to TerraMatrix"',
      'Clear text content',

      // Typography
      'Make text bigger',
      'Set font size to 24',
      'Make text bold',
      'Add italic style',
      'Use Arial font',

      // Alignment
      'Center align text',
      'Align text to the right',
      'Justify text',

      // Colors (Phase 1: Enhanced)
      'Make text blue',
      'Set color to #FF5733',
      'Add yellow background',
      // Extended CSS colors
      'navy text', 'coral text', 'teal',
      'indigo', 'crimson', 'turquoise',
      // Brand colors
      'forest green', 'primary', 'sage',
      'seafoam', 'blue grotto', 'brand',
      // Brightness modifiers
      'darker text', 'lighter text',
      'much darker', 'slightly lighter',
      // Semantic colors
      'success', 'error', 'danger',
      'warning', 'info', 'neutral',
      // HSL support
      'hsl(120, 50, 50)',

      // Decoration
      'Add underline',
      'Apply strikethrough',
      'Remove decoration',

      // Transform
      'Make uppercase',
      'Convert to lowercase',
      'Capitalize text',

      // Spacing
      'Increase line spacing',
      'Set letter spacing to 2',

      // Effects
      'Add text shadow',
      'Set opacity to 0.8',

      // Templates
      'Make it a heading',
      'Apply paragraph style',
      'Create subtitle',

      // Heading shortcuts (HTML-style)
      'h1', 'h2', 'h3', 'h4',

      // Complex
      'Make text bold and blue with size 20',
      'Center align and increase font size',

      // Phase 2.2: Relative font patterns
      'bigger text', 'smaller font',
      '50% bigger font', 'double the font size',
      'increase font by 4', 'one step larger',
      'tighter line spacing', 'looser letter spacing',

      // Phase 2: Advanced Typography
      // 2.1 Font weights
      'thin', 'light', 'medium', 'semibold', 'extrabold', 'black',
      'weight 500', 'font weight 700', 'bolder',

      // 2.2 Font families
      'serif', 'sans-serif', 'monospace',
      'use system font', 'code font',
      'arial', 'helvetica', 'roboto', 'inter',

      // 2.3 Display & text presets
      'display xl', 'display lg', 'display-md',
      'caption', 'overline', 'eyebrow', 'label', 'lead', 'blockquote',

      // 2.4 Line height & letter spacing
      'tight leading', 'loose leading', 'relaxed',
      'wide tracking', 'tight tracking', 'widest tracking',

      // Phase 3: Text Effects & Visual Enhancements
      // 3.1 Text Shadow Presets
      'shadow sm', 'shadow md', 'shadow lg', 'shadow xl',
      'soft shadow', 'hard shadow', 'crisp shadow',
      'red shadow', 'blue shadow', 'purple shadow',
      'glow', 'soft glow', 'neon', 'blue glow', 'green glow',
      'no shadow', 'remove shadow',

      // 3.2 Text Decoration Enhancements
      'wavy underline', 'dashed underline', 'dotted underline', 'double underline',
      'red underline', 'blue underline', 'green underline',
      'thick underline', 'thin underline',
      'add overline', 'overline decoration', 'strikethrough', 'line-through',
      'underline and overline',

      // 3.3 Text Outline & Stroke
      'outline', 'text outline', 'add outline',
      'black outline', 'white outline', 'red outline',
      'thick outline', 'thin outline',
      'no outline', 'remove outline',

      // 3.4 Text Gradient
      'gradient text', 'rainbow text', 'brand gradient',
      'blue to purple gradient', 'red to yellow gradient',
      'no gradient', 'remove gradient',

      // Phase 4: Responsive Text Styling
      // 4.1 Breakpoint-Specific Commands
      'on mobile center', 'on mobile h3', 'on mobile bold',
      'on tablet left', 'on tablet 18px', 'on tablet italic',
      'on desktop h1', 'on desktop 24px', 'on desktop justify',
      'mobile: center', 'tablet: bold', 'desktop: h2',
      'only on mobile uppercase', 'only on desktop underline',
      'mobile bold', 'tablet center', 'desktop h1',
      'mobile 14px', 'tablet 16px', 'desktop 20px',

      // 4.2 Fluid Typography
      'fluid xs', 'fluid sm', 'fluid base', 'fluid lg', 'fluid xl',
      'fluid 2xl', 'fluid 3xl', 'fluid 4xl', 'fluid 5xl',
      'responsive text sm', 'responsive text lg', 'responsive text xl',
      'clamp text base', 'clamp text 2xl',
      'fluid 14 to 20', 'fluid 16 to 24', 'fluid 18px to 28px',
      'responsive 12-18', 'responsive 14-24',
      'fluid text', 'responsive font', 'responsive typography',
      'make text fluid', 'make font responsive',
      'fluid h1', 'fluid h2', 'fluid h3', 'responsive h4',
      'fixed text', 'no fluid', 'remove responsive'
    ];
  }

  async getSuggestions(component: Component): Promise<string[]> {
    const suggestions: string[] = [];

    // Content suggestions
    if (!component.properties?.content) {
      suggestions.push('Set text to "Your content here"');
    }

    // Phase 2.2: Font size suggestions based on current state
    const fontSize = parseComponentFontSize(component);
    if (fontSize < 20) {
      suggestions.push('bigger text', '50% bigger font');
    } else if (fontSize > 24) {
      suggestions.push('smaller text', 'half the font size');
    }

    const fontWeight = component.properties?.fontWeight;
    if (fontWeight !== 'bold') {
      suggestions.push('Make text bold');
    }

    const textAlign = component.properties?.textAlign;
    if (textAlign !== 'center') {
      suggestions.push('Center align text');
    }

    // Phase 1: Enhanced color suggestions
    const currentColor = asString(component.style?.color);
    if (!currentColor) {
      // Suggest brand colors if no color set
      suggestions.push('forest green', 'primary', 'sage');
    } else {
      // Suggest brightness adjustments if color is set
      suggestions.push('darker text', 'lighter text');
    }

    // Phase 1: Semantic color suggestions based on context
    const content = (asString(component.properties?.content) ?? '').toLowerCase();
    if (content.includes('error') || content.includes('fail')) {
      suggestions.push('error color', 'danger');
    } else if (content.includes('success') || content.includes('complete')) {
      suggestions.push('success color', 'positive');
    } else if (content.includes('warning') || content.includes('caution')) {
      suggestions.push('warning color');
    }

    // Phase 2.2: Line height suggestions
    const lineHeight = asNumber(component.properties?.lineHeight) ?? 1.5;
    if (lineHeight > 1.2) {
      suggestions.push('tighter line spacing');
    } else {
      suggestions.push('looser line spacing');
    }

    // Phase 3: Text shadow suggestions
    const textShadow = asString(component.style?.textShadow);
    if (!textShadow || textShadow === 'none') {
      suggestions.push('shadow md', 'soft shadow', 'glow');
    } else {
      suggestions.push('no shadow', 'change to soft shadow');
    }

    // Phase 3: Text decoration suggestions
    const textDecoration = asString(component.style?.textDecoration);
    if (!textDecoration || textDecoration === 'none') {
      suggestions.push('wavy underline', 'dashed underline');
    } else if (textDecoration.includes('underline')) {
      suggestions.push('add overline', 'remove underline');
    }

    // Phase 3: Text outline suggestions
    const webkitTextStroke = asString(component.style?.WebkitTextStroke);
    if (!webkitTextStroke) {
      suggestions.push('add outline', 'black outline');
    } else {
      suggestions.push('remove outline');
    }

    // Phase 3: Text gradient suggestions
    const webkitBackgroundClip = asString(component.style?.WebkitBackgroundClip);
    if (!webkitBackgroundClip || webkitBackgroundClip !== 'text') {
      suggestions.push('gradient text', 'rainbow text');
    } else {
      suggestions.push('remove gradient');
    }

    // Phase 4: Responsive text suggestions
    const currentFontSizeValue = asString(component.style?.fontSize);
    if (currentFontSizeValue && !currentFontSizeValue.includes('clamp')) {
      // Suggest fluid typography if using fixed font size
      suggestions.push('make text fluid', 'fluid lg', 'responsive text');
    } else if (currentFontSizeValue?.includes('clamp')) {
      // Suggest fixed sizing if using fluid
      suggestions.push('fixed text');
    }

    // Breakpoint-specific suggestions
    suggestions.push('on mobile center', 'on desktop h1');

    // Phase 1: Additional color suggestions
    suggestions.push('navy text', 'coral text', 'teal');

    // Template suggestions
    suggestions.push('Make it a heading');

    return suggestions.slice(0, 14); // Increased from 12 to 14 for Phase 4 variety
  }

  /**
   * Override validation for text-specific properties
   */
  protected override validateChanges(changes: PropertyChange, component: Component): { valid: boolean; error?: string } {
    const baseValidation = super.validateChanges(changes, component);
    if (!baseValidation.valid) {
      return baseValidation;
    }

    if (changes.properties?.fontSize) {
      const size = changes.properties.fontSize;
      if (typeof size === 'number' && (size < 8 || size > 72)) {
        return { valid: false, error: "Font size must be between 8 and 72" };
      }
    }

    if (changes.properties?.lineHeight) {
      const height = changes.properties.lineHeight;
      if (typeof height === 'number' && (height < 0.5 || height > 5)) {
        return { valid: false, error: "Line height must be between 0.5 and 5" };
      }
    }

    if (changes.style?.opacity) {
      const opacity = changes.style.opacity;
      if (typeof opacity === 'number' && (opacity < 0 || opacity > 1)) {
        return { valid: false, error: "Opacity must be between 0 and 1" };
      }
    }

    return { valid: true };
  }

  // ==========================================================================
  // Phase 5: Text Selection Command Parsing
  // ==========================================================================

  /**
   * Phase 5.3: Parse commands that target selected text
   * This method handles style application, text replacement, and deletion
   * for the specifically selected portion of text.
   */
  private parseSelectionCommand(
    command: string,
    component: Component,
    selection: TextSelectionContext
  ): CommandResult | null {
    const trimmed = command.trim();
    const lowerCommand = trimmed.toLowerCase();

    // Get the current content (may be string or TextSegment[])
    const currentContent = component.properties?.content;
    const fullText = selection.fullText;

    // Style commands that should apply to selection
    const styleCommands = [
      // Text decoration
      'bold', 'italic', 'underline', 'strikethrough', 'line-through',
      // Font weight variations
      'thin', 'light', 'medium', 'semibold', 'extrabold', 'black-weight',
      // Colors
      'red', 'blue', 'green', 'yellow', 'orange', 'purple', 'pink',
      'black', 'white', 'gray', 'grey', 'navy', 'teal', 'coral',
      'forest', 'sage', 'seafoam', 'primary', 'secondary', 'accent',
      'success', 'error', 'warning', 'info', 'danger',
      // Heading styles - apply font size and weight to selection
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'heading', 'title', 'subtitle', 'display',
      // Font sizes - relative
      'bigger', 'smaller', 'larger', 'tinier',
      // Font sizes - absolute
      'tiny', 'small', 'medium', 'large', 'xl', 'xxl', '2xl', '3xl',
      'xs', 'sm', 'md', 'lg',
      // Font sizes - numeric (will be matched with regex below)
      // Text transforms
      'uppercase', 'lowercase', 'capitalize', 'normal',
      // Other text effects
      'highlight', 'glow', 'shadow', 'outline'
    ];

    // Check if command is a pure style command or ends with a style word
    const matchedStyleWord = styleCommands.find(cmd =>
      lowerCommand === cmd ||
      lowerCommand.startsWith(cmd + ' ') ||
      lowerCommand.endsWith(' ' + cmd)
    );

    // Also check for numeric font size patterns like "12px", "16", "1.5rem"
    const numericSizeMatch = lowerCommand.match(/^(\d+(?:\.\d+)?)(px|rem|em|pt)?$/);

    if (matchedStyleWord || numericSizeMatch) {
      // Special handling for heading commands - use simple inline styles
      const headingStyles: Record<string, { fontSize: string; fontWeight: string | number }> = {
        'h1': { fontSize: '2.5rem', fontWeight: 'bold' },
        'h2': { fontSize: '2rem', fontWeight: 'bold' },
        'h3': { fontSize: '1.75rem', fontWeight: '600' },
        'h4': { fontSize: '1.5rem', fontWeight: '600' },
        'h5': { fontSize: '1.25rem', fontWeight: '600' },
        'h6': { fontSize: '1rem', fontWeight: '600' },
        'title': { fontSize: '2rem', fontWeight: 'bold' },
        'subtitle': { fontSize: '1.25rem', fontWeight: '500' },
        'display': { fontSize: '3rem', fontWeight: 'bold' },
        'heading': { fontSize: '1.5rem', fontWeight: 'bold' },
      };

      // Special handling for font size keywords
      const sizeStyles: Record<string, string> = {
        'tiny': '0.75rem',
        'xs': '0.75rem',
        'small': '0.875rem',
        'sm': '0.875rem',
        'medium': '1rem',
        'md': '1rem',
        'large': '1.25rem',
        'lg': '1.25rem',
        'xl': '1.5rem',
        'xxl': '2rem',
        '2xl': '2rem',
        '3xl': '2.5rem',
      };

      let rangeStyles: TextSegmentStyle = {};

      // Check for heading styles first
      if (matchedStyleWord && headingStyles[matchedStyleWord]) {
        rangeStyles = { ...headingStyles[matchedStyleWord] };
      }
      // Check for size keywords
      else if (matchedStyleWord && sizeStyles[matchedStyleWord]) {
        rangeStyles.fontSize = sizeStyles[matchedStyleWord];
      }
      // Check for numeric font size
      else if (numericSizeMatch) {
        const value = numericSizeMatch[1];
        const unit = numericSizeMatch[2] || 'px';
        rangeStyles.fontSize = `${value}${unit}`;
      }
      // Fall back to pattern matching for other styles
      else {
        // Parse the style changes using existing pattern matching
        let styleChanges = this.matchPatterns(command, component);

        // If full command doesn't match, try just the style word
        // This handles cases like "change the text to blue" where "blue" should match
        if (!styleChanges && matchedStyleWord && matchedStyleWord !== lowerCommand) {
          styleChanges = this.matchPatterns(matchedStyleWord, component);
        }

        if (styleChanges) {
          if (styleChanges.style) {
            // Filter out clamp() and other complex values not suitable for inline styles
            Object.entries(styleChanges.style).forEach(([key, value]) => {
              if (typeof value === 'string' && value.includes('clamp(')) {
                // Skip clamp values - not suitable for inline selection styling
              } else {
                assignTextSegmentStyle(rangeStyles, key, value);
              }
            });
          }
          if (styleChanges.properties) {
            // Some properties map to inline styles
            if (styleChanges.properties.fontWeight) {
              const fontWeight = asStringOrNumber(styleChanges.properties.fontWeight);
              if (fontWeight !== undefined) {
                rangeStyles.fontWeight = fontWeight;
              }
            }
            if (styleChanges.properties.fontStyle) {
              const fontStyle = asString(styleChanges.properties.fontStyle);
              if (fontStyle !== undefined) {
                rangeStyles.fontStyle = fontStyle;
              }
            }
            if (styleChanges.properties.textDecoration) {
              const textDecoration = asString(styleChanges.properties.textDecoration);
              if (textDecoration !== undefined) {
                rangeStyles.textDecoration = textDecoration;
              }
            }
            if (styleChanges.properties.textTransform) {
              const textTransform = asString(styleChanges.properties.textTransform);
              if (textTransform !== undefined) {
                rangeStyles.textTransform = textTransform;
              }
            }
            if (styleChanges.properties.fontSize) {
              // Skip clamp() values
              const fontSize = asStringOrNumber(styleChanges.properties.fontSize);
              if (typeof fontSize === 'string' && fontSize.includes('clamp(')) {
              } else if (fontSize !== undefined) {
                rangeStyles.fontSize = fontSize;
              }
            }
            // Phase 5.3: Also handle color from properties
            if (styleChanges.properties.color) {
              const color = asString(styleChanges.properties.color);
              if (color !== undefined) {
                rangeStyles.color = color;
              }
            }
          }
        }
      }

      // Apply styles to the selected range
      const existingSegments = isRichTextContent(currentContent)
        ? currentContent
        : undefined;

      const newContent = applyStylesToRange(
        fullText,
        selection.startIndex,
        selection.endIndex,
        rangeStyles,
        existingSegments
      );

      return {
        success: true,
        command: {
          type: 'style',
          changes: {
            properties: { content: newContent },
            textSelection: selection,
            textOperation: 'style-range',
            rangeStyles
          },
          confidence: 0.9,
          description: `Apply ${command} to "${selection.selectedText}"`
        }
      };
    }

    // Check for quoted content = replacement of selected text
    const doubleQuoteMatch = trimmed.match(/^"((?:[^"\\]|\\.)*)"\s*$/);
    const singleQuoteMatch = trimmed.match(/^'((?:[^'\\]|\\.)*)'\s*$/);
    const backtickMatch = trimmed.match(/^`([^`]*)`\s*$/);

    const quoteMatch = doubleQuoteMatch || singleQuoteMatch || backtickMatch;

    if (quoteMatch) {
      let replacement = quoteMatch[1];
      // Handle escapes for double/single quotes
      if (doubleQuoteMatch) {
        replacement = replacement.replace(/\\"/g, '"').replace(/\\\\/g, '\\');
      } else if (singleQuoteMatch) {
        replacement = replacement.replace(/\\'/g, "'").replace(/\\\\/g, '\\');
      }

      const newText = this.replaceSelectedText(
        fullText,
        selection.startIndex,
        selection.endIndex,
        replacement
      );

      return {
        success: true,
        command: {
          type: 'content',
          changes: {
            properties: { content: newText },
            textSelection: selection,
            textOperation: 'replace'
          },
          confidence: 0.95,
          description: `Replace "${selection.selectedText}" with "${replacement}"`
        }
      };
    }

    // Check for delete/remove commands
    if (/^(delete|remove|clear)(\s+selection|\s+text|\s+selected)?$/i.test(lowerCommand)) {
      const newText = this.replaceSelectedText(
        fullText,
        selection.startIndex,
        selection.endIndex,
        ''
      );


      return {
        success: true,
        command: {
          type: 'content',
          changes: {
            properties: { content: newText },
            textSelection: selection,
            textOperation: 'delete'
          },
          confidence: 0.9,
          description: `Delete "${selection.selectedText}"`
        }
      };
    }

    // Check for "make selection [style]" patterns
    const makeSelectionMatch = lowerCommand.match(/^make\s+(?:selection|selected|it)\s+(.+)$/i);
    if (makeSelectionMatch) {
      const styleCommand = makeSelectionMatch[1];
      // Recursively call with just the style command
      return this.parseSelectionCommand(styleCommand, component, selection);
    }

    // Check for "apply [style] to selection" patterns
    const applyToSelectionMatch = lowerCommand.match(/^apply\s+(.+)\s+to\s+(?:selection|selected)$/i);
    if (applyToSelectionMatch) {
      const styleCommand = applyToSelectionMatch[1];
      return this.parseSelectionCommand(styleCommand, component, selection);
    }

    // No selection-specific pattern matched
    return null;
  }

  /**
   * Phase 5.3: Helper to replace text at specified range
   * @param fullText The complete text content
   * @param startIndex Start of range to replace
   * @param endIndex End of range to replace (exclusive)
   * @param replacement The replacement text
   */
  private replaceSelectedText(
    fullText: string,
    startIndex: number,
    endIndex: number,
    replacement: string
  ): string {
    const before = fullText.slice(0, startIndex);
    const after = fullText.slice(endIndex);
    const result = before + replacement + after;
    return result;
  }
}

export const textCommandHandler = new TextCommandHandler();
